import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  Upload,
  MessageSquare,
  Settings,
  Database,
  Plus,
  X,
  Trash2,
  Search,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Send,
  Phone,
  Info,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Clock,
  Edit2,
  Copy,
  Check,
  Facebook,
  Instagram,
  Mail,
  FileText,
  Download,
  FileSpreadsheet
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { DateTime } from "luxon";
import { Pharmacy, Shift, WhatsAppLog, DiagResponse } from "./types";
import { compressImage, formatBytes } from "./imageOptimizer";
import PDFGenerator from "./PDFGenerator";
import { EmailInbox } from "./components/EmailInbox";
import { OriginalExcelUploader } from "./components/OriginalExcelUploader";
import { WhatsAppActivation } from "./components/WhatsAppActivation";
import { BotMessageEditor } from "./components/BotMessageEditor";
import { adminFetch } from "./api";

// Load Firebase Client libraries
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import firebaseConfig from "../firebase-applet-config.json";

// Initialize Client-side Firebase safely
const clientApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

function getSafeAuth() {
  try {
    return getAuth(clientApp);
  } catch (e: any) {
    console.warn(
      "Could not getAuth directly, possibly due to sandboxed iframe. Falling back...",
      e,
    );
    // Return a dummy object if auth completely fails, or let signInWithPopup fail later
    return null;
  }
}

const ImageUploaderWidget: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  idPrefix: string;
  showCompressionToggle?: boolean;
  defaultCompress?: boolean;
}> = ({ label, value, onChange, idPrefix, showCompressionToggle, defaultCompress = true }) => {
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldCompress, setShouldCompress] = useState(defaultCompress);
  const [savingStats, setSavingStats] = useState<{
    original: string;
    optimized: string;
    percent?: number;
    isRawGif?: boolean;
  } | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setCompressing(true);
    setError(null);
    setSavingStats(null);

    let fileToUpload = file;
    const isGif = file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");
    const compressThis = shouldCompress && !isGif;

    try {
      // 1. Client-side compression & optimization (if compression is enabled and not a GIF)
      if (compressThis && file.type.startsWith("image/")) {
        try {
          const optResult = await compressImage(file, {
            maxWidth: 1200,
            maxHeight: 1200,
            quality: 0.75, // balanced quality/speed compression
            convertTo: "image/webp", // modern high efficiency standard
          });

          fileToUpload = optResult.file;

          if (optResult.ratio > 0) {
            setSavingStats({
              original: formatBytes(optResult.originalSize),
              optimized: formatBytes(optResult.compressedSize),
              percent: optResult.ratio,
              isRawGif: false,
            });
          }
        } catch (compErr) {
          console.warn(
            "Client-side image optimizer failed, uploading original:",
            compErr,
          );
        }
      } else if (isGif || !shouldCompress) {
        setSavingStats({
          original: formatBytes(file.size),
          optimized: formatBytes(file.size),
          isRawGif: isGif,
        });
      }

      setCompressing(false);

      // 2. Transmit file to backend with compression preference
      const formData = new FormData();
      formData.append("photo", fileToUpload);
      formData.append("compress", compressThis ? "true" : "false");
      
      const res = await adminFetch("/api/upload-photo", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Fallo al subir la imagen");
      }
      const data = await res.json();
      onChange(data.url);
    } catch (err: any) {
      setError(err?.message || "Error al subir");
    } finally {
      setUploading(false);
      setCompressing(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="block text-slate-500 uppercase font-bold font-mono text-[10px]">
          {label}
        </label>
        {showCompressionToggle && (
          <label className="flex items-center gap-1.5 text-[10px] text-slate-600 font-mono cursor-pointer select-none bg-slate-100/80 px-2 py-0.5 rounded-md hover:bg-slate-200/80 transition-colors">
            <input
              type="checkbox"
              checked={shouldCompress}
              onChange={(e) => setShouldCompress(e.target.checked)}
              className="h-3 w-3 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
            />
            <span>Comprimir imagen (desactivar para GIFs animados)</span>
          </label>
        )}
      </div>

      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        className="border border-dashed border-slate-200 rounded-xl p-4 bg-slate-50 hover:bg-slate-100/70 transition-colors cursor-pointer relative flex flex-col items-center justify-center min-h-[110px]"
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          id={`${idPrefix}-file-input`}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-1.5 pointer-events-none">
            <RefreshCw className="animate-spin text-emerald-500" size={20} />
            <span className="text-[10px] text-slate-500 font-bold font-mono">
              {compressing ? "Optimizando foto..." : "Subiendo foto..."}
            </span>
            {savingStats && savingStats.percent !== undefined && (
              <span className="text-[9px] text-emerald-600 font-bold font-mono">
                ¡Ratio: -{savingStats.percent}% !
              </span>
            )}
          </div>
        ) : value ? (
          <div className="w-full flex items-center gap-3 relative z-10">
            <img
              src={value}
              alt="Preview"
              width="80"
              height="56"
              className="h-14 w-20 object-contain bg-slate-900/5 rounded-lg border border-slate-200"
              style={{ aspectRatio: "80/56" }}
              referrerPolicy="no-referrer"
            />
            <div className="flex-1 min-w-0">
              <span className="block text-[10px] font-bold text-slate-600 font-mono truncate">
                {value}
              </span>
              {savingStats?.isRawGif ? (
                <span className="inline-block text-[9px] font-bold text-indigo-600 font-mono bg-indigo-50 px-1.5 py-0.5 rounded-md mt-0.5 border border-indigo-100">
                  🎞️ Formato GIF original preservado ({savingStats.original})
                </span>
              ) : savingStats?.percent ? (
                <span className="inline-block text-[9px] font-bold text-emerald-600 font-mono bg-emerald-50 px-1.5 py-0.5 rounded-md mt-0.5 border border-emerald-100">
                  ✨ Optimizado: -{savingStats.percent}% ({savingStats.original}{" "}
                  ➔ {savingStats.optimized})
                </span>
              ) : null}
              <div className="mt-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onChange("");
                    setSavingStats(null);
                  }}
                  className="text-[10px] font-bold text-red-500 hover:text-red-600 font-mono relative z-20 cursor-pointer"
                >
                  Eliminar Imagen
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center pointer-events-none space-y-1">
            <Upload className="mx-auto text-slate-400" size={20} />
            <p className="text-[10px] font-bold text-slate-500 font-mono">
              Arrastra una foto o GIF aquí o haz{" "}
              <span className="text-emerald-500">clic para buscar</span>
            </p>
            <p className="text-[9px] text-slate-400 font-mono">
              Soporta PNG, GIF, JPG, WEBP (Ajuste recomendado 320x720)
            </p>
          </div>
        )}
      </div>
      {error && (
        <p className="text-[10px] font-bold text-red-500 font-mono mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
}

// Geolocation and distance utility functions for Tandil
function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d;
}

// Safe LocalStorage helpers to avoid "The operation is insecure" or SecurityError in sandboxed/third-party contexts
function safeGetLocalStorage(key: string): string | null {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return localStorage.getItem(key);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
  return null;
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(key, value);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
}

function safeRemoveLocalStorage(key: string): void {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn("localStorage is not accessible:", e);
  }
}

async function geocodeAddress(
  direccion: string,
  nombre: string,
): Promise<{ lat: number; lng: number } | null> {
  const cacheKey = `geo_pharmacy_${direccion.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
  if (typeof window !== "undefined") {
    const cached = safeGetLocalStorage(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        // ignore
      }
    }
  }

  try {
    const query = `${direccion}, Tandil, Argentina`;
    const res = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
        if (typeof window !== "undefined") {
          safeSetLocalStorage(cacheKey, JSON.stringify(coords));
        }
        return coords;
      }
    }
  } catch (err) {
    console.error("Geocoding failed for:", direccion, err);
  }
  
  // Fallback to random coordinate close to Tandil if Nominatim fails
  return {
    lat: -37.32 + (Math.random() * 0.02 - 0.01),
    lng: -59.13 + (Math.random() * 0.02 - 0.01)
  };
}

export default function App() {
  // Authentication state
  const [currentUser, setCurrentUser] = useState<GoogleUser | null>(() => {
    if (typeof window !== "undefined") {
      const saved = safeGetLocalStorage("tandil_admin_user");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (_) {}
      }
    }
    return null;
  });

  // Save session state locally
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (currentUser) {
      safeSetLocalStorage("tandil_admin_user", JSON.stringify(currentUser));
    } else {
      safeRemoveLocalStorage("tandil_admin_user");
    }
  }, [currentUser]);

  // Navigation & View Status
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    new Date().getMonth() + 1,
  ); // 1-indexed (Jan = 1)

  // App data state
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [diagData, setDiagData] = useState<DiagResponse | null>(null);

  // Public Design/Banner Config State
  const [frontpageConfig, setFrontpageConfig] = useState({
    topBannerHeight: 90,
    topBannerImage: "",
    topBannerMobileImage: "",
    topBannerBgColor: "#0f172a",
    topBannerText: "Farmacias de Turno en Tandil",
    topBannerLink: "",
    hideTopBannerMobile: false,
    hideTopBannerDesktop: false,
    locationBoxPosition: "above",
    bottomBannerHeight: 170,
    bottomBannerImage: "",
    bottomBannerMobileImage: "",
    bottomBannerBgColor: "#1e293b",
    bottomBannerText: "",
    bottomBannerLink: "",
    detailsBoxColor: "#10b981",
    showWhatsAppBanner: false,
    whatsAppBannerPosition: "top",
    whatsAppBannerText:
      "📲 Consulta quién está de guardia hoy o mañana cómodamente por WhatsApp.",
    whatsAppConsultPhone: "",
    whatsAppConsultDefaultText: "Hoy",
    showWhatsAppFloatingButton: false,
    pharmacyFontSize: "sm",
    pharmacyFontWeight: "extrabold",
    pharmacyFontColor: "white",
    pharmaciesPerRow: 2,
    carouselDesktop: false,
    addressFontSize: "sm",
    addressFontWeight: "semibold",
    addressFontColor: "white",
    phoneFontSize: "sm",
    phoneFontWeight: "semibold",
    phoneFontColor: "white",
    showPhotoOnMobile: true,
    hideCountdownFrame: false,
    countdownBelowToggle: false,
    footerLogoUrl: "",
    footerAddress: "",
    footerPhone: "",
    footerEmail: "",
    footerSocialsText: "Seguinos en redes sociales",
    footerFacebookUrl: "",
    footerInstagramUrl: "",
    footerBgColor: "#1e293b",
    footerCopyrightText: "© 2026 FarmaTurno Tandil - Todos los derechos reservados.",
    footerCopyrightLink: "",
    promoModuleEnabled: false,
    promoModulePosition: "above_location",
    promoModuleImage: "",
    promoModuleDesktopImage: "",
    promoModuleLink: "",
    promoModuleAlt: "Módulo publicitario",
    googleAnalyticsId: "",
    adminEmail: "",
    googleClientId: "",
  });

  // Customize View form
  const [customizeForm, setCustomizeForm] = useState({
    topBannerHeight: 90,
    topBannerImage: "",
    topBannerMobileImage: "",
    topBannerBgColor: "#0f172a",
    topBannerText: "Farmacias de Turno en Tandil",
    topBannerLink: "",
    hideTopBannerMobile: false,
    hideTopBannerDesktop: false,
    locationBoxPosition: "above",
    bottomBannerHeight: 170,
    bottomBannerImage: "",
    bottomBannerMobileImage: "",
    bottomBannerBgColor: "#1e293b",
    bottomBannerText: "",
    bottomBannerLink: "",
    detailsBoxColor: "#10b981",
    showWhatsAppBanner: false,
    whatsAppBannerPosition: "top",
    whatsAppBannerText:
      "📲 Consulta quién está de guardia hoy o mañana cómodamente por WhatsApp.",
    whatsAppConsultPhone: "",
    whatsAppConsultDefaultText: "Hoy",
    showWhatsAppFloatingButton: false,
    pharmacyFontSize: "sm",
    pharmacyFontWeight: "extrabold",
    pharmacyFontColor: "white",
    pharmaciesPerRow: 2,
    carouselDesktop: false,
    addressFontSize: "sm",
    addressFontWeight: "semibold",
    addressFontColor: "white",
    phoneFontSize: "sm",
    phoneFontWeight: "semibold",
    phoneFontColor: "white",
    showPhotoOnMobile: true,
    hideCountdownFrame: false,
    countdownBelowToggle: false,
    footerLogoUrl: "",
    footerAddress: "",
    footerPhone: "",
    footerEmail: "",
    footerSocialsText: "Seguinos en redes sociales",
    footerFacebookUrl: "",
    footerInstagramUrl: "",
    footerBgColor: "#1e293b",
    footerCopyrightText: "© 2026 FarmaTurno Tandil - Todos los derechos reservados.",
    footerCopyrightLink: "",
    promoModuleEnabled: false,
    promoModulePosition: "above_location",
    promoModuleImage: "",
    promoModuleDesktopImage: "",
    promoModuleLink: "",
    promoModuleAlt: "Módulo publicitario",
    googleAnalyticsId: "",
    adminEmail: "",
    googleClientId: "",
  });
  const [saveCustomizeStatus, setSaveCustomizeStatus] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);
  const [savingCustomize, setSavingCustomize] = useState(false);

  // Community View state
  const [viewingNextShift, setViewingNextShift] = useState(false);
  const [carouselStartIndex, setCarouselStartIndex] = useState<number>(0);
  const [carouselDirection, setCarouselDirection] = useState<"next" | "prev">(
    "next",
  );
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  // Geolocation & Distance States for Tandil
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [distanceByPharmId, setDistanceByPharmId] = useState<{
    [id: string]: number;
  }>({});
  const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCarouselStartIndex(0);
    setCarouselDirection("next");
  }, [viewingNextShift]);

  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({ hours: 0, minutes: 0, seconds: 0 });
  const [currentClockTime, setCurrentClockTime] = useState<string>("");

  // UI States
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<
    "public" | "calendar" | "catalog" | "whatsapp" | "customize" | "pdf" | "inbox"
  >("public");

  // Listen to popstate or hashchange to enter /admin automatically
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUrlChange = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      if (path === "/admin" || hash === "#/admin" || hash === "#admin") {
        if (activeTab === "public") {
          setActiveTab("calendar");
        }
      }
    };
    handleUrlChange();
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener("hashchange", handleUrlChange);
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener("hashchange", handleUrlChange);
    };
  }, [activeTab]);

  // CSV Import state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadMonth, setUploadMonth] = useState<string>(
    String(new Date().getMonth() + 1).padStart(2, "0"),
  );
  const [showOriginalExcelUploader, setShowOriginalExcelUploader] = useState(false);
  const [uploadYear, setUploadYear] = useState<string>(
    String(new Date().getFullYear()),
  );
  const [uploadStatus, setUploadStatus] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Edit Shift states
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [shiftSearchQuery, setShiftSearchQuery] = useState<string>("");

  // Catalog item editing states
  const [catalogSearch, setCatalogSearch] = useState<string>("");
  const [editPharmacyMode, setEditPharmacyMode] = useState<boolean>(false);
  const [draggingOverPharmId, setDraggingOverPharmId] = useState<string | null>(
    null,
  );
  const [pendingPhotoUpload, setPendingPhotoUpload] = useState<{
    pharm: Pharmacy;
    file: File;
  } | null>(null);
  const [catalogForm, setCatalogForm] = useState<{
    id?: string;
    nombre: string;
    direccion: string;
    telefono: string;
    locationUrl?: string;
    fotoUrl?: string;
    colorHex?: string;
    whatsapp?: string;
  }>({
    nombre: "",
    direccion: "",
    telefono: "",
    locationUrl: "",
    fotoUrl: "",
    colorHex: "#10b981",
    whatsapp: "",
  });

  // WhatsApp Simulation States
  const [simPhone, setSimPhone] = useState<string>("5492494123456");
  const [simText, setSimText] = useState<string>("hola");
  const [simStatus, setSimStatus] = useState<{
    loading?: boolean;
    text?: string;
  } | null>(null);

  // Meta WhatsApp Credentials Config Interface
  const [isConfigOpen, setIsConfigOpen] = useState<boolean>(false);
  const [configForm, setConfigForm] = useState({
    phoneId: "",
    verifyToken: "TandilVerify",
    accessToken: "",
    keepArgentina9: true,
    argentinaFormat: "keep9",
  });
  const [saveConfigStatus, setSaveConfigStatus] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);
  const [savingConfig, setSavingConfig] = useState<boolean>(false);
  const [clearingLogs, setClearingLogs] = useState<boolean>(false);
  const [showClearConfirm, setShowClearConfirm] = useState<boolean>(false);
  // Ocultar del historial los mensajes que envía el bot, para leer solo los entrantes
  const [hideBotLogs, setHideBotLogs] = useState<boolean>(
    () => safeGetLocalStorage("tandil_hide_bot_logs") === "1",
  );
  useEffect(() => {
    safeSetLocalStorage("tandil_hide_bot_logs", hideBotLogs ? "1" : "0");
  }, [hideBotLogs]);
  const visibleLogs = (diagData?.logs || []).filter(
    (log) => !hideBotLogs || log.direction !== "outbound",
  );

  // Auto-reset clear logs confirm state after 4 seconds
  useEffect(() => {
    if (showClearConfirm) {
      const timer = setTimeout(() => {
        setShowClearConfirm(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showClearConfirm]);

  const [clearingMonthConfirm, setClearingMonthConfirm] =
    useState<boolean>(false);
  const [copiedWebhook, setCopiedWebhook] = useState<boolean>(false);
  const [copiedProdWebhook, setCopiedProdWebhook] = useState<boolean>(false);
  const [copiedToken, setCopiedToken] = useState<boolean>(false);

  // Fetch the current credentials to prefill when editing
  const handleOpenConfig = async () => {
    setSaveConfigStatus(null);
    try {
      const res = await adminFetch("/api/whatsapp/config");
      if (res.ok) {
        const data = await res.json();
        setConfigForm({
          phoneId: data.phoneId || "",
          verifyToken: data.verifyToken || "TandilVerify",
          accessToken: "", // Omit private credentials returning from wire by default
          keepArgentina9: data.keepArgentina9 !== false,
          argentinaFormat: data.argentinaFormat || "keep9",
        });
      }
    } catch (err) {
      console.error("No se pudo cargar la configuración:", err);
    }
    setIsConfigOpen(true);
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConfig(true);
    setSaveConfigStatus(null);
    try {
      const res = await adminFetch("/api/whatsapp/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(configForm),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSaveConfigStatus({
          success: true,
          message: "¡Credenciales actualizadas correctamente!",
        });
        await fetchData(); // Sync UI diagnostics monitor immediately
        setTimeout(() => {
          setIsConfigOpen(false);
          setSaveConfigStatus(null);
        }, 1500);
      } else {
        setSaveConfigStatus({
          success: false,
          message: data.error || "Ocurrió un error al guardar.",
        });
      }
    } catch (err: any) {
      setSaveConfigStatus({
        success: false,
        message: err?.message || "Fallo de conexión.",
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleClearLogs = async () => {
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      return;
    }
    setShowClearConfirm(false);
    setClearingLogs(true);
    try {
      const res = await adminFetch("/api/whatsapp/logs/clear", {
        method: "POST",
      });
      if (res.ok) {
        // Reload diagData immediately to show cleared state
        const dgRes = await adminFetch("/api/whatsapp/diag");
        if (dgRes.ok) {
          const dgData = await dgRes.json();
          setDiagData(dgData);
        }
      } else {
        console.error("Error al intentar limpiar los logs.");
      }
    } catch (err: any) {
      console.error("Error de conexión al intentar limpiar los logs:", err);
    } finally {
      setClearingLogs(false);
    }
  };

  // Helper phone sanitizer
  const sanitizePhoneNumber = (num: any): string => {
    if (!num) return "";
    return String(num).replace(/[^0-9]/g, "");
  };

  // Safe client calculations for Argentina timezone
  const getActiveShiftDateClient = (offsetDays = 0): string => {
    let localNow = DateTime.now().setZone("America/Argentina/Buenos_Aires");
    if (localNow.hour < 8 || (localNow.hour === 8 && localNow.minute < 30)) {
      localNow = localNow.minus({ days: 1 });
    }
    if (offsetDays !== 0) {
      localNow = localNow.plus({ days: offsetDays });
    }
    return localNow.toFormat("yyyy-MM-dd");
  };

  // Geolocation and navigation trigger functions
  const handleCalculateClosest = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocationError(
        "Tu dispositivo o navegador no soporta la Geolocalización.",
      );
      return;
    }
    setLoadingLocation(true);
    setLocationError(null);

    try {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;

          // Identify currently active pharmacies to geocode
          const activePharmIds = viewingNextShift
            ? shifts.find((s) => s.date === getActiveShiftDateClient(1))
                ?.pharmacyIds || []
            : shifts.find((s) => s.date === getActiveShiftDateClient(0))
                ?.pharmacyIds || [];
          const activePharmList = pharmacies.filter((p) =>
            activePharmIds.includes(p.id),
          );

          const updatedDistances: { [id: string]: number } = {};
          for (let i = 0; i < activePharmList.length; i++) {
            const pharm = activePharmList[i];
            if (i > 0) {
              // Respect Nominatim's 1 request per second policy
              await new Promise(resolve => setTimeout(resolve, 1100));
            }
            const coords = await geocodeAddress(pharm.direccion, pharm.nombre);
            if (coords) {
              const dist = calculateHaversineDistance(
                userLat,
                userLng,
                coords.lat,
                coords.lng,
              );
              updatedDistances[pharm.id] = parseFloat(dist.toFixed(2));
            }
          }

          // Wait to update the UI until ALL calculations are done
          setUserLocation({ lat: userLat, lng: userLng });
          setDistanceByPharmId(updatedDistances);
          setLoadingLocation(false);
        },
        (error) => {
          console.error("Error getting geolocation:", error);
          let msg =
            "No pudimos acceder a tu ubicación. Asegurate de habilitar los permisos en tu navegador.";
          if (error.code === error.PERMISSION_DENIED) {
            msg =
              "Permiso denegado. Por favor, habilitá el acceso a la ubicación en la configuración de la página.";
          }
          setLocationError(msg);
          setLoadingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    } catch (err: any) {
      console.error("Security/Geolocation error:", err);
      setLocationError("Operación denegada por seguridad (ej. iframe bloqueado). Intenta abrir la app en una nueva pestaña.");
      setLoadingLocation(false);
    }
  };

  const handleClearLocation = () => {
    setUserLocation(null);
    setDistanceByPharmId({});
    setLocationError(null);
  };

  useEffect(() => {
    if (userLocation) {
      const updateActiveDistances = async () => {
        const activePharmIds = viewingNextShift
          ? shifts.find((s) => s.date === getActiveShiftDateClient(1))
              ?.pharmacyIds || []
          : shifts.find((s) => s.date === getActiveShiftDateClient(0))
              ?.pharmacyIds || [];
        const activePharmList = pharmacies.filter((p) =>
          activePharmIds.includes(p.id),
        );

        const updatedDistances = { ...distanceByPharmId };
        let updated = false;

        for (const pharm of activePharmList) {
          if (updatedDistances[pharm.id] === undefined) {
            const coords = await geocodeAddress(pharm.direccion, pharm.nombre);
            if (coords) {
              const dist = calculateHaversineDistance(
                userLocation.lat,
                userLocation.lng,
                coords.lat,
                coords.lng,
              );
              updatedDistances[pharm.id] = parseFloat(dist.toFixed(2));
              updated = true;
            }
          }
        }

        if (updated) {
          setDistanceByPharmId(updatedDistances);
        }
      };
      updateActiveDistances();
    }
  }, [viewingNextShift, userLocation, pharmacies, shifts]);

  // Helper handles frontpage style submissions
  const handleSaveCustomize = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCustomize(true);
    setSaveCustomizeStatus(null);
    try {
      const res = await adminFetch("/api/frontpage/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customizeForm),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSaveCustomizeStatus({
          success: true,
          message: "¡Diseño y anuncios actualizados correctamente!",
        });
        setFrontpageConfig(data.config);
        setCustomizeForm(data.config);
      } else {
        setSaveCustomizeStatus({
          success: false,
          message: data.error || "Ocurrió un inconveniente al guardar.",
        });
      }
    } catch (err: any) {
      setSaveCustomizeStatus({
        success: false,
        message: err?.message || "Error de conexión con el servidor.",
      });
    } finally {
      setSavingCustomize(false);
    }
  };

  // Load backend data
  const fetchData = async () => {
    setRefreshing(true);
    try {
      // Las tres consultas en paralelo; las fotos llegan aparte como links /foto/...
      const [phRes, shRes, fpRes] = await Promise.all([
        fetch("/api/pharmacies"),
        fetch("/api/shifts"),
        fetch("/api/frontpage/config"),
      ]);
      if (phRes.ok) {
        const phData = await phRes.json();
        setPharmacies(phData);
      }

      if (shRes.ok) {
        const shData = await shRes.json();
        setShifts(shData);
      }

      if (fpRes.ok) {
        const fpData = await fpRes.json();
        setFrontpageConfig(fpData);
        setCustomizeForm(fpData);
      }
    } catch (err) {
      console.error("Failed to sync client data from server:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Google Analytics script injection
  useEffect(() => {
    const gaId = frontpageConfig?.googleAnalyticsId;
    if (!gaId || gaId.trim() === "") return;

    // Check if gtag script already loaded
    const scriptId = "google-analytics-script";
    let existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      // 1. Create main script load
      const gaScript = document.createElement("script");
      gaScript.id = scriptId;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      gaScript.async = true;
      document.head.appendChild(gaScript);

      // 2. Create inline script initialization
      const inlineScript = document.createElement("script");
      inlineScript.id = "google-analytics-inline-script";
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaId}', { page_path: window.location.pathname });
      `;
      document.head.appendChild(inlineScript);
    }
  }, [frontpageConfig?.googleAnalyticsId]);

  useEffect(() => {
    fetchData();

    // High precision Argentina Countdown to next 08:30 AM
    const updateCountdown = () => {
      const now = DateTime.now().setZone("America/Argentina/Buenos_Aires");
      let target = now.set({ hour: 8, minute: 30, second: 0, millisecond: 0 });
      if (now > target) {
        target = target.plus({ days: 1 });
      }
      const diff = target.diff(now, ["hours", "minutes", "seconds"]).toObject();
      setTimeLeft({
        hours: Math.max(0, Math.floor(diff.hours || 0)),
        minutes: Math.max(0, Math.floor(diff.minutes || 0)),
        seconds: Math.max(0, Math.floor(diff.seconds || 0)),
      });
      setCurrentClockTime(now.toFormat("HH:mm:ss"));
    };
    updateCountdown();
    const countdownTimer = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(countdownTimer);
    };
  }, []);

  // Diagnóstico de WhatsApp: solo con un administrador logueado, y con sondeo
  // periódico solo en la pestaña del bot. (Antes lo consultaban TODOS los visitantes
  // cada 4 s, lo que agotaba la cuota diaria de lecturas de Firestore.)
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    const loadDiag = async () => {
      try {
        const dgRes = await adminFetch("/api/whatsapp/diag");
        if (dgRes.ok && !cancelled) {
          setDiagData(await dgRes.json());
        }
      } catch (_) {}
    };
    loadDiag();
    if (activeTab !== "whatsapp") {
      return () => {
        cancelled = true;
      };
    }
    const interval = setInterval(loadDiag, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentUser, activeTab]);

  // Calendar calculations
  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month, 0).getDate();
  const getFirstDayOffset = (year: number, month: number) => {
    // 0 = Sunday, 1 = Monday, etc.
    const day = new Date(year, month - 1, 1).getDay();
    // Shift so 0 = Monday, 6 = Sunday
    return day === 0 ? 6 : day - 1;
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOffset = getFirstDayOffset(currentYear, currentMonth);

  const monthNames = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Drag and Drop files
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const copyToClipboard = async (text: string, setCopiedState: (v: boolean) => void) => {
    let success = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        success = true;
      } catch (err) {
        console.warn("Clipboard API failed, trying fallback", err);
      }
    }
    
    if (!success) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "absolute";
        textArea.style.left = "-999999px";
        document.body.prepend(textArea);
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        success = true;
      } catch (error) {
        console.error("Fallback copy failed", error);
      }
    }

    if (success) {
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
    }
  };

  // CSV submit
  const handleCSVUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;
    setLoading(true);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("month", uploadMonth);
    formData.append("year", uploadYear);
    formData.append("skipPharmacyUpdate", "true");

    try {
      const res = await adminFetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      const resData = await res.json();
      if (res.ok) {
        setUploadStatus({ success: true, message: resData.message });
        setUploadFile(null);
        await fetchData();
      } else {
        setUploadStatus({
          success: false,
          message: resData.error || "Fallo al procesar el archivo.",
        });
      }
    } catch (e: any) {
      setUploadStatus({
        success: false,
        message: "Error de red al establecer conexión con el backend.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    let csvContent = "Farmacia,Día,Dirección,Teléfono\n";
    pharmacies.forEach((p) => {
      const nombre = `"${p.nombre.replace(/"/g, '""')}"`;
      const direccion = `"${p.direccion.replace(/"/g, '""')}"`;
      const telefono = `"${p.telefono.replace(/"/g, '""')}"`;
      csvContent += `${nombre},,${direccion},${telefono}\n`;
    });
    
    // Add BOM for Excel UTF-8 support
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `plantilla_farmacias_${uploadMonth}_${uploadYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add pharmacy to shift date
  const handleAddPharmacyToDate = async (pharmacyId: string) => {
    if (!selectedDate) return;
    const formattedDate = selectedDate;

    const existingShift = shifts.find((s) => s.date === formattedDate);
    let updatedPharmacyIds = [pharmacyId];
    if (existingShift) {
      if (existingShift.pharmacyIds.includes(pharmacyId)) return; // already added
      updatedPharmacyIds = [...existingShift.pharmacyIds, pharmacyId];
    }

    try {
      const res = await adminFetch("/api/shifts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: formattedDate,
          pharmacyIds: updatedPharmacyIds,
        }),
      });
      if (res.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Remove pharmacy from shift date
  const handleRemovePharmacyFromDate = async (pharmacyId: string) => {
    if (!selectedDate) return;
    const formattedDate = selectedDate;
    const existingShift = shifts.find((s) => s.date === formattedDate);
    if (!existingShift) return;

    const updatedPharmacyIds = existingShift.pharmacyIds.filter(
      (id) => id !== pharmacyId,
    );

    try {
      if (updatedPharmacyIds.length === 0) {
        // Delete shift row completely
        const res = await adminFetch(`/api/shifts/${formattedDate}`, {
          method: "DELETE",
        });
        if (res.ok) await fetchData();
      } else {
        const res = await adminFetch("/api/shifts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: formattedDate,
            pharmacyIds: updatedPharmacyIds,
          }),
        });
        if (res.ok) await fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Clear all shifts for the selected month
  const handleClearMonth = async (year: number, month: number) => {
    const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
    try {
      setLoading(true);
      const res = await adminFetch(`/api/shifts/month/${yearMonth}`, {
        method: "DELETE",
      });
      if (res.ok) {
        await fetchData();
        setSelectedDate(null);
      }
    } catch (err) {
      console.error("Error al limpiar el mes:", err);
    } finally {
      setLoading(false);
      setClearingMonthConfirm(false);
    }
  };

  // Catalog item submission
  const handleCatalogFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catalogForm.nombre || !catalogForm.direccion) return;

    setLoading(true);
    try {
      const res = await adminFetch("/api/pharmacies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(catalogForm),
      });

      if (res.ok) {
        setCatalogForm({
          nombre: "",
          direccion: "",
          telefono: "",
          locationUrl: "",
          fotoUrl: "",
          colorHex: "#10b981",
          whatsapp: "",
        });
        setEditPharmacyMode(false);
        await fetchData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete pharmacy from catalog
  const handleDeletePharmacy = async (id: string) => {
    if (
      !confirm(
        "¿Seguro quieres remover esta farmacia? Esto removerá también sus turnos cargados.",
      )
    )
      return;
    setLoading(true);
    try {
      const res = await adminFetch(`/api/pharmacies/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Drag-and-drop cover photo function for catalog list
  const handlePharmacyPhotoDrop = async (pharm: Pharmacy, file: File) => {
    if (!file.type.startsWith("image/")) {
      alert(
        "Por favor, arrastra un archivo de tipo imagen (*.png, *.jpg, *.jpeg, *.webp).",
      );
      return;
    }
    setPendingPhotoUpload({ pharm, file });
  };

  const confirmPharmacyPhotoUpload = async () => {
    if (!pendingPhotoUpload) return;
    const { pharm, file } = pendingPhotoUpload;
    setPendingPhotoUpload(null);

    setLoading(true);
    try {
      let fileToUpload = file;
      let savingsLog = "";
      try {
        const optResult = await compressImage(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          quality: 0.75,
          convertTo: "image/webp",
        });
        fileToUpload = optResult.file;
        if (optResult.ratio > 0) {
          savingsLog = ` ¡Foto optimizada! Ahorro del ${optResult.ratio}% (${formatBytes(optResult.originalSize)} ➔ ${formatBytes(optResult.compressedSize)})`;
        }
      } catch (e) {
        console.warn("Error optimize photo drop, upload standard instead", e);
      }

      const formData = new FormData();
      formData.append("photo", fileToUpload);

      const res = await adminFetch("/api/upload-photo", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "No se pudo subir la foto");
      }

      const data = await res.json();

      // Update pharmacy with returned URL
      const updatedPharm = { ...pharm, fotoUrl: data.url };
      const saveRes = await adminFetch("/api/pharmacies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedPharm),
      });

      if (saveRes.ok) {
        await fetchData();
        if (savingsLog) {
          // You could also turn this into a toast, but keeping alert for now
          // alert(`Imagen actualizada con éxito.${savingsLog}`);
        }
      } else {
        throw new Error("No se pudo actualizar la farmacia en el catálogo");
      }
    } catch (error: any) {
      alert(`Error al actualizar la foto: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp simulation request
  const handleSimulateMsg = async () => {
    if (!simText.trim()) return;
    setSimStatus({ loading: true });

    try {
      // Send a simulated inbound message with from/body
      const res = await adminFetch("/api/whatsapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entry: [
            {
              changes: [
                {
                  value: {
                    messages: [
                      {
                        from: simPhone.trim(),
                        type: "text",
                        text: { body: simText.trim() },
                      },
                    ],
                  },
                },
              ],
            },
          ],
        }),
      });

      if (res.ok) {
        setSimStatus({
          text: `Mensaje de simulación procesado en el servidor con éxito.`,
        });
        await fetchData();
      } else {
        setSimStatus({
          text: `El servidor retornó un error al procesar el mensaje simulado.`,
        });
      }
    } catch (err: any) {
      setSimStatus({ text: `Fallo de conexión: ${err.message}` });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-850 font-sans selection:bg-emerald-500 selection:text-white">
      {/* Dynamic Community Frontpage conditional portal */}
      {activeTab === "public" &&
        (() => {
          // Typography maps
          const sizeMap: Record<string, string> = {
            xs: "text-[11px]",
            sm: "text-xs",
            base: "text-sm",
            lg: "text-base",
            xl: "text-lg",
          };
          const weightMap: Record<string, string> = {
            normal: "font-normal",
            medium: "font-medium",
            semibold: "font-semibold",
            bold: "font-bold",
            extrabold: "font-extrabold",
          };
          const colorMap: Record<string, string> = {
            white: "text-white",
            "slate-900": "text-slate-900",
            "slate-800": "text-slate-800",
            "emerald-900": "text-[#064e3b]",
            "yellow-300": "text-[#d97706]",
          };

          const fontSizeClass =
            sizeMap[frontpageConfig.pharmacyFontSize] || "text-sm";
          const fontWeightClass =
            weightMap[frontpageConfig.pharmacyFontWeight] || "font-extrabold";
          const fontColorClass =
            colorMap[frontpageConfig.pharmacyFontColor] || "text-white";

          const addressFontSizeClass =
            sizeMap[frontpageConfig.addressFontSize || ""] || "text-xs";
          const addressFontWeightClass =
            weightMap[frontpageConfig.addressFontWeight || ""] ||
            "font-semibold";
          const addressFontColorClass =
            colorMap[frontpageConfig.addressFontColor || ""] || "text-white";

          const phoneFontSizeClass =
            sizeMap[frontpageConfig.phoneFontSize || ""] || "text-xs";
          const phoneFontWeightClass =
            weightMap[frontpageConfig.phoneFontWeight || ""] || "font-semibold";
          const textPhoneColorClass =
            colorMap[frontpageConfig.phoneFontColor || ""] || "text-white";

          const gridColsClass =
            frontpageConfig.pharmaciesPerRow === 1
              ? "grid-cols-1 max-w-xl mx-auto"
              : frontpageConfig.pharmaciesPerRow === 3
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : frontpageConfig.pharmaciesPerRow === 4
                  ? "grid-cols-1 md:grid-cols-3 lg:grid-cols-4"
                  : "grid-cols-1 md:grid-cols-2"; // default: 2

          const detailsColor = frontpageConfig.detailsBoxColor || "#10b981";

          // Calculate global WhatsApp link
          let globalWaPhone = "";
          if (frontpageConfig.whatsAppConsultPhone) {
            globalWaPhone = sanitizePhoneNumber(
              frontpageConfig.whatsAppConsultPhone,
            );
          } else {
            globalWaPhone = diagData?.config?.WHATSAPP_PHONE_NUMBER_ID
              ? sanitizePhoneNumber(diagData.config.WHATSAPP_PHONE_NUMBER_ID)
              : "";
          }
          const globalWaDefaultText =
            frontpageConfig.whatsAppConsultDefaultText || "Hoy";
          const globalWaLink = `https://wa.me/${globalWaPhone}?text=${encodeURIComponent(globalWaDefaultText)}`;

          // WhatsApp promotional banner template
          const renderWhatsAppPromoBanner = () => {
            if (loading || !frontpageConfig.showWhatsAppBanner) return null;

            return (
              <div className="w-full flex flex-col items-center justify-center text-center px-4 py-6 bg-transparent">
                <p className="text-sm md:text-base font-semibold text-slate-700 leading-relaxed max-w-2xl">
                  {frontpageConfig.whatsAppBannerText ||
                    "📲 Consulta quién está de guardia hoy o mañana cómodamente por WhatsApp."}
                </p>
                <div className="mt-3">
                  <a
                    href={globalWaLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full text-xs font-extrabold transition-all shadow-md transform hover:scale-103 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MessageSquare size={13} />
                    Chatear por WhatsApp
                  </a>
                </div>
              </div>
            );
          };

          const renderTopBanner = () => {
            if (
              frontpageConfig.hideTopBannerMobile &&
              frontpageConfig.hideTopBannerDesktop
            ) {
              return null;
            }

            const hasImage =
              !!frontpageConfig.topBannerImage ||
              !!frontpageConfig.topBannerMobileImage;
            const desktopImg = frontpageConfig.topBannerImage;
            const mobileImg =
              frontpageConfig.topBannerMobileImage || desktopImg;

            let visibilityClass = "flex";
            if (frontpageConfig.hideTopBannerMobile) {
              visibilityClass = "hidden md:flex";
            } else if (frontpageConfig.hideTopBannerDesktop) {
              visibilityClass = "flex md:hidden";
            }

            return (
              <div
                onClick={() => {
                  if (frontpageConfig.topBannerLink) {
                    window.open(frontpageConfig.topBannerLink, "_blank");
                  }
                }}
                style={{
                  height: hasImage
                    ? "auto"
                    : `${frontpageConfig.topBannerHeight || 90}px`,
                  backgroundColor: hasImage
                    ? "transparent"
                    : frontpageConfig.topBannerBgColor || "#0f172a",
                  cursor: frontpageConfig.topBannerLink ? "pointer" : "default",
                }}
                className={`w-full items-center justify-center overflow-hidden relative border-0 ${visibilityClass}`}
              >
                {hasImage ? (
                  <picture className="w-full text-center block">
                    {frontpageConfig.topBannerMobileImage && (
                      <source
                        media="(max-width: 767px)"
                        srcSet={frontpageConfig.topBannerMobileImage}
                      />
                    )}
                    <img
                      src={desktopImg || mobileImg}
                      alt="Banner Superior"
                      referrerPolicy="no-referrer"
                      width="1200"
                      height={frontpageConfig.topBannerHeight || 90}
                      className="max-w-full h-auto object-contain mx-auto block border-0"
                      style={{
                        maxHeight: `${frontpageConfig.topBannerHeight || 90}px`,
                        aspectRatio: `1200/${frontpageConfig.topBannerHeight || 90}`,
                      }}
                    />
                  </picture>
                ) : (
                  <div
                    id="top-banner-content"
                    className="relative z-10 text-center px-4 py-2"
                  >
                    <h2 className="text-sm md:text-lg font-bold text-white tracking-wide truncate max-w-full drop-shadow-md">
                      {frontpageConfig.topBannerText ||
                        "Farmacias de Turno en Tandil"}
                    </h2>
                  </div>
                )}
              </div>
            );
          };

          const renderLocationLocator = () => {
            return (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-900">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 shrink-0">
                    <MapPin
                      size={20}
                      className={loadingLocation ? "animate-bounce" : ""}
                    />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                      🧭 Ubicación y Distancias
                    </h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      {userLocation
                        ? "Calculando distancias en tiempo real desde tu posición. Las farmacias de guardia se ordenaron por cercanía."
                        : "Calculá la distancia real en KM a tu farmacia más cercana."}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-center sm:justify-end">
                  {userLocation ? (
                    <button
                      onClick={handleClearLocation}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-200"
                    >
                      <X size={13} />
                      Limpiar ubicación
                    </button>
                  ) : (
                    <button
                      onClick={handleCalculateClosest}
                      disabled={loadingLocation}
                      className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm md:text-base font-extrabold rounded-2xl transition-all flex items-center gap-2 shadow-md hover:shadow-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-95"
                    >
                      {loadingLocation ? (
                        <>
                          <RefreshCw size={18} className="animate-spin" />
                          Localizando...
                        </>
                      ) : (
                        <>
                          <MapPin size={18} />
                          ¿Cuál me queda más cerca?
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          };

          const renderSecondBanner = () => {
            const hasImage =
              !!frontpageConfig.bottomBannerImage ||
              !!frontpageConfig.bottomBannerMobileImage;
            const hasText = !!frontpageConfig.bottomBannerText;
            if (!hasImage && !hasText) return null;

            const desktopImg = frontpageConfig.bottomBannerImage;
            const mobileImg =
              frontpageConfig.bottomBannerMobileImage || desktopImg;

            return (
              <div
                onClick={() => {
                  if (frontpageConfig.bottomBannerLink) {
                    window.open(frontpageConfig.bottomBannerLink, "_blank");
                  }
                }}
                style={{
                  height: hasImage
                    ? "auto"
                    : `${frontpageConfig.bottomBannerHeight || 170}px`,
                  backgroundColor: hasImage
                    ? "transparent"
                    : frontpageConfig.bottomBannerBgColor || "#1e293b",
                  cursor: frontpageConfig.bottomBannerLink
                    ? "pointer"
                    : "default",
                }}
                className="w-full flex items-center justify-center overflow-hidden relative mt-4 border-0"
              >
                {hasImage ? (
                  <picture className="w-full text-center block">
                    {frontpageConfig.bottomBannerMobileImage && (
                      <source
                        media="(max-width: 767px)"
                        srcSet={frontpageConfig.bottomBannerMobileImage}
                      />
                    )}
                    <img
                      src={desktopImg || mobileImg}
                      alt="Segundo Banner"
                      referrerPolicy="no-referrer"
                      width="1200"
                      height={frontpageConfig.bottomBannerHeight || 170}
                      className="max-w-full h-auto object-contain mx-auto block border-0"
                      style={{
                        maxHeight: `${frontpageConfig.bottomBannerHeight || 170}px`,
                        aspectRatio: `1200/${frontpageConfig.bottomBannerHeight || 170}`,
                      }}
                    />
                  </picture>
                ) : (
                  <div
                    id="second-banner-content"
                    className="relative z-10 text-center px-4 py-4"
                  >
                    <p className="text-sm md:text-base font-semibold text-white leading-relaxed mb-1 drop-shadow-sm">
                      {frontpageConfig.bottomBannerText}
                    </p>
                  </div>
                )}
              </div>
            );
          };

          const renderCountdownCard = () => {
            const containerStyle = frontpageConfig.hideCountdownFrame
              ? "bg-transparent border-none p-0 shadow-none flex flex-col justify-center items-center text-center gap-5 w-full mx-auto"
              : "bg-white rounded-2xl border border-slate-200 p-6 shadow-xs flex flex-col justify-center items-center text-center gap-6 w-full mx-auto";

            const itemBg = frontpageConfig.hideCountdownFrame
              ? "bg-slate-50 border border-slate-100 shadow-xs"
              : "bg-slate-100 border border-slate-200 shadow-inner";

            return (
              <div className={containerStyle}>
                <div className="space-y-2 flex flex-col items-center">
                  <div className="flex items-center justify-center gap-2">
                    <span
                      className={`h-3 w-3 rounded-full ${viewingNextShift ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`}
                    ></span>
                    <h3
                      className={`text-base md:text-lg uppercase font-black tracking-widest font-mono ${viewingNextShift ? "text-amber-600" : "text-[#10b981]"}`}
                    >
                      {viewingNextShift
                        ? "Siguiente Turno"
                        : "Turno Activo Ahora"}
                    </h3>
                  </div>
                  <p className="text-xs text-slate-500 max-w-md">
                    El cambio ocurre de forma automática todos los días a las
                    08:30 AM.
                  </p>
                </div>

                {/* TIME BAR & CLOCK GRID CONTAINER */}
                <div className="w-full flex flex-col md:flex-row items-center justify-center gap-6 max-w-2xl">
                  {/* CURRENT CLOCK */}
                  <div className="flex flex-col items-center gap-1.5 w-full md:w-auto">
                    <div className="text-[10px] text-slate-400 uppercase font-extrabold tracking-widest font-mono">
                      Hora Actual
                    </div>
                    <div
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-xl ${itemBg}`}
                    >
                      <Clock size={16} className="text-slate-400 shrink-0" />
                      <span className="font-mono text-2xl md:text-3xl font-extrabold text-[#111827] tracking-wider">
                        {currentClockTime || "08:30:00"}
                      </span>
                    </div>
                  </div>

                  <div className="hidden md:block h-8 w-px bg-slate-200" />

                  {/* COUNTDOWN */}
                  <div className="flex flex-col items-center gap-1.5 w-full md:w-auto">
                    <div className="text-[10px] text-slate-400 uppercase font-extrabold tracking-widest font-mono">
                      Tiempo Restante en Guardia
                    </div>
                    <div className="inline-flex gap-2 justify-center">
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-slate-800 tracking-tight font-sans">
                          {String(timeLeft.hours).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          hs
                        </span>
                      </div>
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-slate-800 tracking-tight font-sans">
                          {String(timeLeft.minutes).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          min
                        </span>
                      </div>
                      <div
                        className={`rounded-xl px-4 py-2 min-w-[70px] text-center ${itemBg}`}
                      >
                        <span className="block text-2xl md:text-3xl font-mono font-bold text-emerald-600 tracking-tight font-sans">
                          {String(timeLeft.seconds).padStart(2, "0")}
                        </span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 font-mono">
                          seg
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          };

          const renderPromoModule = () => {
            if (
              !frontpageConfig.promoModuleEnabled ||
              (!frontpageConfig.promoModuleImage &&
                !frontpageConfig.promoModuleDesktopImage)
            ) {
              return null;
            }

            const mobileImg = frontpageConfig.promoModuleImage;
            const desktopImg = frontpageConfig.promoModuleDesktopImage;
            const altText =
              frontpageConfig.promoModuleAlt || "Módulo publicitario";

            const card = (
              <div className="w-full flex justify-center items-center">
                {desktopImg && mobileImg ? (
                  <div className="w-full">
                    {/* Mobile Display (320x720 o formato vertical) */}
                    <div className="block md:hidden w-full max-w-md mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white group transition-all duration-300 hover:shadow-md">
                      <img
                        src={mobileImg}
                        alt={altText}
                        className="w-full h-auto max-h-[720px] object-contain rounded-2xl block select-none mx-auto"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                    {/* Desktop Display (1230x140 o formato horizontal) */}
                    <div className="hidden md:flex w-full max-w-7xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white justify-center items-center group transition-all duration-300 hover:shadow-md">
                      <img
                        src={desktopImg}
                        alt={altText}
                        className="w-full h-auto max-h-[220px] object-contain rounded-2xl block select-none mx-auto"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </div>
                  </div>
                ) : desktopImg ? (
                  <div className="w-full max-w-7xl mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white flex justify-center items-center group transition-all duration-300 hover:shadow-md">
                    <img
                      src={desktopImg}
                      alt={altText}
                      className="w-full h-auto max-h-[220px] object-contain rounded-2xl block select-none mx-auto"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-full max-w-md mx-auto overflow-hidden rounded-2xl border border-slate-200 shadow-xs bg-white flex flex-col justify-center items-center group transition-all duration-300 hover:shadow-md">
                    <img
                      src={mobileImg}
                      alt={altText}
                      className="w-full h-auto max-h-[720px] object-contain rounded-2xl block select-none"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            );

            return (
              <div className="w-full flex justify-center my-2 md:my-8 md:py-2">
                {frontpageConfig.promoModuleLink ? (
                  <a
                    href={frontpageConfig.promoModuleLink}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-full max-w-7xl cursor-pointer no-underline focus:outline-hidden"
                  >
                    {card}
                  </a>
                ) : (
                  card
                )}
              </div>
            );
          };

          return (
            <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between font-sans selection:bg-emerald-500 selection:text-white pb-0">
              <div>
                {/* 1. TOP BANNER */}
                {renderTopBanner()}

                {/* 2. SECOND BANNER */}
                {renderSecondBanner()}

                {/* 3. WHATSAPP BANNER AT TOP POSITION */}
                {(frontpageConfig.whatsAppBannerPosition === "top" ||
                  frontpageConfig.whatsAppBannerPosition === "superior") && (
                  <div className="pt-2 md:pt-4">
                    {renderWhatsAppPromoBanner()}
                  </div>
                )}

                {/* PUBLIC WORKSPACE CONTAINER */}
                <main className="max-w-7xl mx-auto px-4 pt-3 pb-8 md:pt-6 md:pb-8 space-y-6 md:space-y-8">
                  {/* CURRENT DATE SHIFT HEADER & COUNTDOWN (UPPER PLACEHOLDER) */}
                  {!frontpageConfig.countdownBelowToggle &&
                    renderCountdownCard()}

                  {/* 🧭 CLOSEST PHARMACY LOCATOR CONTROL BAR */}
                  {frontpageConfig.locationBoxPosition !== "below" && (
                    <div className="space-y-4">
                      {frontpageConfig.promoModulePosition === "above_location" &&
                        renderPromoModule()}
                      {renderLocationLocator()}
                      {frontpageConfig.promoModulePosition === "below_location" &&
                        renderPromoModule()}

                      {/* GEOLOCATION ERROR MESSAGE CONTAINER */}
                      {locationError && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl flex items-start justify-between gap-3 shadow-xs">
                          <div className="flex items-start gap-3">
                            <AlertCircle
                              className="text-rose-600 shrink-0 mt-0.5"
                              size={16}
                            />
                            <div className="space-y-1">
                              <p className="text-xs font-semibold">
                                {locationError}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setLocationError(null)}
                            className="text-rose-500 hover:text-rose-700 font-mono text-xs font-bold leading-none cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* PROMO MODULE WHEN POSITIONED ABOVE PHARMACIES */}
                  {frontpageConfig.promoModulePosition === "above_pharmacies" &&
                    renderPromoModule()}

                  {/* NEXT SHIFT WARNING NOTICE IF ACTIVE */}
                  {viewingNextShift && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-start gap-4 shadow-xs">
                      <Info
                        className="text-amber-600 shrink-0 mt-0.5"
                        size={16}
                      />
                      <div className="space-y-1">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900">
                          Aviso: Farmacias del Próximo Turno
                        </h4>
                        <p className="text-xs text-slate-600">
                          Estás consultando los locales que estarán de guardia
                          mañana, de <b>8:30 AM</b> a <b>8:30 AM</b> del día
                          siguiente.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* PHARMACIES RENDERING GRID OR CAROUSEL */}
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3">
                      <RefreshCw
                        className="animate-spin text-emerald-500"
                        size={32}
                      />
                      <p className="text-xs text-slate-400 font-mono">
                        Cargando farmacias de Tandil...
                      </p>
                    </div>
                  ) : (viewingNextShift
                      ? shifts.find(
                          (s) => s.date === getActiveShiftDateClient(1),
                        )?.pharmacyIds || []
                      : shifts.find(
                          (s) => s.date === getActiveShiftDateClient(0),
                        )?.pharmacyIds || []
                    ).length === 0 ? (
                    <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-xs">
                      <AlertCircle
                        className="mx-auto text-amber-500 mb-3 animate-pulse"
                        size={36}
                      />
                      <h3 className="text-sm font-bold text-slate-800">
                        Sin Guardia Registrada
                      </h3>
                      <p className="text-xs text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">
                        Lo siento, nuestro cronograma del mes no contiene turnos
                        registrados para esa fecha. Vuelve a consultar más tarde
                        o contacta al administrador.
                      </p>
                    </div>
                  ) : (
                    (() => {
                      const activePharmIds = viewingNextShift
                        ? shifts.find(
                            (s) => s.date === getActiveShiftDateClient(1),
                          )?.pharmacyIds || []
                        : shifts.find(
                            (s) => s.date === getActiveShiftDateClient(0),
                          )?.pharmacyIds || [];

                      let activePharmList = pharmacies.filter((p) =>
                        activePharmIds.includes(p.id),
                      );

                      if (userLocation) {
                        activePharmList = [...activePharmList].sort((a, b) => {
                          const distA =
                            distanceByPharmId[a.id] !== undefined
                              ? distanceByPharmId[a.id]
                              : Infinity;
                          const distB =
                            distanceByPharmId[b.id] !== undefined
                              ? distanceByPharmId[b.id]
                              : Infinity;
                          return distA - distB;
                        });
                      }

                      // Identificamos la farmacia más cercana y la más lejana de las que se calculó la distancia
                      const validDistancePharms = activePharmList.filter(
                        (p) => distanceByPharmId[p.id] !== undefined,
                      );
                      const closestPharmId =
                        validDistancePharms.length > 0
                          ? validDistancePharms[0].id
                          : null;
                      const farthestPharmId =
                        validDistancePharms.length > 1
                          ? validDistancePharms[validDistancePharms.length - 1]
                              .id
                          : null;

                      const renderPharmacyCard = (pharm: Pharmacy) => {
                        const fallbackBg =
                          "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=600&q=80";
                        const bgPhoto = pharm.fotoUrl || fallbackBg;
                        const showPhotoOnMobile =
                          frontpageConfig.showPhotoOnMobile !== false;

                        return (
                          <div
                            key={pharm.id}
                            className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col shadow-xs h-full"
                          >
                            {/* Box 1: Photo Background with White name superimposed */}
                            <div
                              style={{ backgroundImage: `url(${bgPhoto})` }}
                              className={`h-44 bg-cover bg-center relative w-full items-center justify-center shrink-0 ${showPhotoOnMobile ? "flex" : "hidden md:flex"}`}
                            >
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
                              <div className="relative z-10 text-center px-4 w-full">
                                <h2 className="text-2xl font-black uppercase tracking-wider text-white select-all text-shadow notranslate" translate="no">
                                  {pharm.nombre}
                                </h2>
                              </div>
                            </div>

                            {/* Box 2: Detail Box in configurable color */}
                            <div
                              style={{ backgroundColor: detailsColor }}
                              className="p-5 flex-1 flex flex-col justify-between"
                            >
                              <div className="space-y-4">
                                {/* Mobile title when photo is hidden */}
                                {!showPhotoOnMobile && (
                                  <div className="md:hidden border-b border-white/20 pb-2 mb-2">
                                    <h2 className="text-xl font-extrabold uppercase tracking-wider text-white select-all notranslate" translate="no">
                                      {pharm.nombre}
                                    </h2>
                                  </div>
                                )}

                                {/* Phone with Phone icon (now first and centered) */}
                                <div className="flex flex-col items-center justify-center text-center">
                                  <a
                                    href={
                                      pharm.whatsapp
                                        ? `https://wa.me/${sanitizePhoneNumber(pharm.whatsapp)}?text=${encodeURIComponent(`Hola Farmacia ${pharm.nombre}, vi que están de turno hoy en Tandil y quería realizar una consulta.`)}`
                                        : `tel:${pharm.telefono ? String(pharm.telefono).replace(/[^0-9+]/g, "") : ""}`
                                    }
                                    target={
                                      pharm.whatsapp ? "_blank" : undefined
                                    }
                                    rel="noreferrer"
                                    className={`inline-flex items-center gap-2 justify-center hover:opacity-85 transition-opacity ${phoneFontSizeClass} ${phoneFontWeightClass} ${textPhoneColorClass}`}
                                  >
                                    <Phone className="shrink-0" size={15} />
                                    <span>{pharm.telefono}</span>
                                  </a>
                                </div>

                                {/* Address with MapPin icon (below phone, centered, clickable to Google Maps) */}
                                <div className="flex flex-col items-center justify-center text-center mt-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const url = userLocation
                                        ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`
                                        : pharm.locationUrl ||
                                          `https://www.google.com/maps/search/?api=1&query=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`;
                                      window.open(url, "_blank");
                                    }}
                                    className={`inline-flex items-center gap-2 justify-center hover:opacity-85 cursor-pointer transition-opacity ${addressFontSizeClass} ${addressFontWeightClass} ${addressFontColorClass} border-none bg-transparent p-0 outline-none`}
                                  >
                                    <MapPin className="shrink-0" size={15} />
                                    <span>{pharm.direccion}</span>
                                  </button>

                                  {/* Distancia real en Km o metros con tags especiales de cercanía */}
                                  {distanceByPharmId[pharm.id] !==
                                    undefined && (
                                    <div className="mt-3 flex flex-col items-center gap-1.5 select-none w-full">
                                      {pharm.id === closestPharmId && (
                                        <span className="bg-emerald-500 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs animate-pulse font-sans flex items-center gap-1 border border-emerald-450">
                                          <span>📍</span> Más cercana
                                        </span>
                                      )}
                                      {pharm.id === farthestPharmId && (
                                        <span className="bg-amber-400 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3 py-0.5 rounded-full shadow-xs font-sans flex items-center gap-1 border border-amber-300">
                                          <span>📍</span> Más lejana
                                        </span>
                                      )}

                                      <div className="text-xs font-black text-white bg-slate-950/45 border border-white/20 backdrop-blur-md py-1.5 px-4 rounded-full inline-flex items-center gap-1 font-mono hover:scale-105 transition-all shadow-xs shrink-0 select-all">
                                        {distanceByPharmId[pharm.id] < 1 ? (
                                          <span>
                                            A{" "}
                                            {Math.round(
                                              distanceByPharmId[pharm.id] *
                                                1000,
                                            )}{" "}
                                            metros
                                          </span>
                                        ) : (
                                          <span>
                                            A {distanceByPharmId[pharm.id]} km
                                            de distancia
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="flex gap-2 mt-5 pt-4 border-t border-white/20">
                                {/* Google Maps Trigger */}
                                <button
                                  onClick={() => {
                                    const url = userLocation
                                      ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`
                                      : pharm.locationUrl ||
                                        `https://www.google.com/maps/search/?api=1&query=Farmacia+${encodeURIComponent(pharm.nombre)}+${encodeURIComponent(pharm.direccion)}+Tandil`;
                                    window.open(url, "_blank");
                                  }}
                                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white/10 hover:bg-white/20 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow-xs border border-white/5 cursor-pointer"
                                >
                                  <MapPin size={12} />
                                  {userLocation
                                    ? "¿Cómo llegar?"
                                    : "Google Maps"}
                                </button>

                                {/* Optional WhatsApp Trigger */}
                                {pharm.whatsapp && (
                                  <button
                                    onClick={() => {
                                      window.open(
                                        `https://wa.me/${sanitizePhoneNumber(pharm.whatsapp)}?text=${encodeURIComponent(`Hola Farmacia ${pharm.nombre}, vi que están de turno hoy en Tandil y quería realizar una consulta.`)}`,
                                        "_blank",
                                      );
                                    }}
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-lg text-xs font-bold transition-all shadow-xs cursor-pointer"
                                  >
                                    <MessageSquare size={12} />
                                    WhatsApp
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      };

                      const configuredPerPage =
                        frontpageConfig.pharmaciesPerRow || 2;
                      let dynamicPerPage = configuredPerPage;
                      if (windowWidth >= 768 && windowWidth < 1024) {
                        // El formato tablet de pantallas md (768px a 1023px) puede mostrar como máximo 2 farmacias por fila cleanly.
                        // Si hay más farmacias activas que el límite de 1 fila (máx 2), activamos el carrusel para evitar saltos de línea.
                        dynamicPerPage = Math.min(configuredPerPage, 2);
                      } else if (windowWidth < 768) {
                        dynamicPerPage = 1;
                      }

                      const useCarousel =
                        frontpageConfig.carouselDesktop ||
                        activePharmList.length > dynamicPerPage;

                      if (useCarousel) {
                        const maxStart = Math.max(
                          0,
                          activePharmList.length - dynamicPerPage,
                        );
                        const safeStartIndex = Math.min(
                          carouselStartIndex,
                          maxStart,
                        );
                        const visiblePharmacies = activePharmList.slice(
                          safeStartIndex,
                          safeStartIndex + dynamicPerPage,
                        );

                        return (
                          <div>
                            {/* Desktop View: Slide Carousel */}
                            <div className="hidden md:block relative px-10">
                              {activePharmList.length > dynamicPerPage && (
                                <>
                                  {/* Left Arrow Button (Infinite loop) */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCarouselDirection("prev");
                                      setCarouselStartIndex((prev) =>
                                        prev === 0 ? maxStart : prev - 1,
                                      );
                                    }}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-10 w-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                                  >
                                    <ChevronLeft size={20} />
                                  </button>

                                  {/* Right Arrow Button (Infinite loop) */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCarouselDirection("next");
                                      setCarouselStartIndex((prev) =>
                                        prev >= maxStart ? 0 : prev + 1,
                                      );
                                    }}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-10 w-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md text-slate-700 hover:bg-slate-50 transition-all cursor-pointer"
                                  >
                                    <ChevronRight size={20} />
                                  </button>
                                </>
                              )}

                              <AnimatePresence mode="wait">
                                <motion.div
                                  key={`${safeStartIndex}-${viewingNextShift}`}
                                  initial={{
                                    opacity: 0,
                                    x: carouselDirection === "next" ? 60 : -60,
                                  }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{
                                    opacity: 0,
                                    x: carouselDirection === "next" ? -60 : 60,
                                  }}
                                  transition={{
                                    duration: 0.35,
                                    ease: [0.25, 1, 0.5, 1],
                                  }}
                                  className="flex flex-wrap justify-center gap-6"
                                >
                                  {visiblePharmacies.map((p) => {
                                    const widthClass =
                                      dynamicPerPage === 1
                                        ? "w-full max-w-xl"
                                        : dynamicPerPage === 2
                                          ? "w-full md:w-[calc(50%-12px)]"
                                          : dynamicPerPage === 3
                                            ? "w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                                            : "w-full md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)]";
                                    return (
                                      <div
                                        key={p.id}
                                        className={`${widthClass} h-stretch`}
                                      >
                                        {renderPharmacyCard(p)}
                                      </div>
                                    );
                                  })}
                                </motion.div>
                              </AnimatePresence>

                              {/* Dots / Indicators */}
                              {activePharmList.length > dynamicPerPage && (
                                <div className="flex justify-center gap-1.5 mt-6">
                                  {Array.from({
                                    length:
                                      activePharmList.length -
                                      dynamicPerPage +
                                      1,
                                  }).map((_, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => {
                                        setCarouselDirection(
                                          idx > safeStartIndex
                                            ? "next"
                                            : "prev",
                                        );
                                        setCarouselStartIndex(idx);
                                      }}
                                      className={`h-2 rounded-full transition-all duration-300 ${safeStartIndex === idx ? "w-5 bg-emerald-500" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Mobile View: Standard stacked scroll list (one beneath another) as requested */}
                            <div className="md:hidden flex flex-col gap-6">
                              {activePharmList.map((p) =>
                                renderPharmacyCard(p),
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Standard static grid column layout
                      const columns = frontpageConfig.pharmaciesPerRow || 2;
                      if (activePharmList.length < columns) {
                        return (
                          <div className="flex flex-wrap justify-center gap-6">
                            {activePharmList.map((p) => {
                              const widthClass =
                                columns === 1
                                  ? "w-full max-w-xl"
                                  : columns === 2
                                    ? "w-full md:w-[calc(50%-12px)] max-w-md"
                                    : columns === 3
                                      ? "w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-sm"
                                      : "w-full md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)] max-w-[280px]";
                              return (
                                <div
                                  key={p.id}
                                  className={`${widthClass} h-stretch`}
                                >
                                  {renderPharmacyCard(p)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Standard static grid column layout
                      return (
                        <div className={`grid ${gridColsClass} gap-6`}>
                          {activePharmList.map((p) => renderPharmacyCard(p))}
                        </div>
                      );
                    })()
                  )}

                  {/* PROMO MODULE WHEN POSITIONED BELOW PHARMACIES */}
                  {frontpageConfig.promoModulePosition === "below_pharmacies" &&
                    renderPromoModule()}

                  {/* 🧭 CLOSEST PHARMACY LOCATOR CONTROL BAR (LOWER PLACEHOLDER) */}
                  {frontpageConfig.locationBoxPosition === "below" && (
                    <div className="pt-4 border-t border-slate-200 mt-4 space-y-4">
                      {frontpageConfig.promoModulePosition === "above_location" &&
                        renderPromoModule()}
                      {renderLocationLocator()}
                      {frontpageConfig.promoModulePosition === "below_location" &&
                        renderPromoModule()}

                      {/* GEOLOCATION ERROR MESSAGE CONTAINER */}
                      {locationError && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-800 p-4 rounded-xl flex items-start justify-between gap-3 shadow-xs">
                          <div className="flex items-start gap-3">
                            <AlertCircle
                              className="text-rose-600 shrink-0 mt-0.5"
                              size={16}
                            />
                            <div className="space-y-1">
                              <p className="text-xs font-semibold">
                                {locationError}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setLocationError(null)}
                            className="text-rose-500 hover:text-rose-700 font-mono text-xs font-bold leading-none cursor-pointer"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* NEXT / CURRENT TOGGLE BUTTON IN COMMUNITY VIEW */}
                  {/* NEXT / CURRENT TOGGLE BUTTON IN COMMUNITY VIEW WITH BELOW COUNTDOWN SUPPORT */}
                  <div className="flex flex-col items-center gap-6 pt-8 border-t border-slate-200">
                    <div className="flex justify-center">
                      {!viewingNextShift ? (
                        <button
                          onClick={() => setViewingNextShift(true)}
                          className="px-6 py-3 border border-slate-200 bg-white hover:bg-slate-50 text-emerald-600 hover:text-emerald-700 text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                        >
                          <CalendarIcon size={14} />
                          Ver farmacias del siguiente turno
                        </button>
                      ) : (
                        <button
                          onClick={() => setViewingNextShift(false)}
                          className="px-6 py-3 border border-slate-200 bg-white hover:bg-slate-50 text-blue-600 hover:text-blue-700 text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                        >
                          <RefreshCw size={14} />
                          Volver al turno de hoy
                        </button>
                      )}
                    </div>

                    {/* Countdown Card placed here conditionally */}
                    {frontpageConfig.countdownBelowToggle && (
                      <div className="w-full max-w-3xl mt-2">
                        {renderCountdownCard()}
                      </div>
                    )}
                  </div>
                </main>

                {/* 4. WHATSAPP BANNER AT BOTTOM POSITION */}
                {(frontpageConfig.whatsAppBannerPosition === "bottom" ||
                  frontpageConfig.whatsAppBannerPosition === "inferior") && (
                  <>
                    <div className="h-6" />
                    {renderWhatsAppPromoBanner()}
                  </>
                )}
              </div>

              {/* COMMUNITY FOOTER & BACKEND LOGIN */}
              <footer
                style={{
                  backgroundColor: frontpageConfig.footerBgColor || "#1e293b",
                }}
                className="py-12 px-6 mt-16 transition-all text-white border-t border-slate-150"
              >
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 items-stretch font-sans text-sm text-white">
                  {/* LADO IZQUIERDO: Logo, Dirección, Teléfono, Email */}
                  <div className="space-y-4 text-left">
                    {frontpageConfig.footerLogoUrl && (
                      <img
                        src={frontpageConfig.footerLogoUrl}
                        alt="Logo de Farmacia"
                        width="200"
                        height="64"
                        className="max-h-16 object-contain"
                        style={{ aspectRatio: "200/64" }}
                        referrerPolicy="no-referrer"
                      />
                    )}
                    <div className="space-y-2 opacity-90 text-xs text-white">
                      {frontpageConfig.footerAddress && (
                        <div className="flex items-center gap-2 text-white">
                          <MapPin
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerAddress}
                          </span>
                        </div>
                      )}
                      {frontpageConfig.footerPhone && (
                        <div className="flex items-center gap-2 text-white">
                          <Phone
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerPhone}
                          </span>
                        </div>
                      )}
                      {frontpageConfig.footerEmail && (
                        <div className="flex items-center gap-2 text-white">
                          <Mail
                            size={14}
                            className="shrink-0 text-emerald-400 text-white"
                          />
                          <span className="text-white">
                            {frontpageConfig.footerEmail}
                          </span>
                        </div>
                      )}
                      {!frontpageConfig.footerAddress &&
                        !frontpageConfig.footerPhone &&
                        !frontpageConfig.footerEmail && (
                          <p className="text-white/40 italic">
                            Información de contacto no configurada
                          </p>
                        )}
                    </div>
                  </div>

                  {/* CENTRO: Redes Sociales */}
                  <div className="flex flex-col items-center justify-center text-center space-y-4 border-y md:border-y-0 md:border-x border-white/10 py-6 md:py-0 px-4">
                    <h4 className="font-bold text-sm tracking-wider uppercase opacity-80 text-white">
                      {frontpageConfig.footerSocialsText ||
                        "Seguinos en redes sociales"}
                    </h4>
                    <div className="flex items-center gap-4">
                      {frontpageConfig.footerFacebookUrl ? (
                        <a
                          href={frontpageConfig.footerFacebookUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:scale-105 transition-all text-white"
                          title="Facebook"
                        >
                          <Facebook size={18} />
                        </a>
                      ) : (
                        <span className="text-xs text-white/40 italic text-white/50">
                          Facebook no configurado
                        </span>
                      )}

                      {frontpageConfig.footerInstagramUrl ? (
                        <a
                          href={frontpageConfig.footerInstagramUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 hover:scale-105 transition-all text-white"
                          title="Instagram"
                        >
                          <Instagram size={18} />
                        </a>
                      ) : (
                        <span className="text-xs text-white/40 italic text-white/50">
                          Instagram no configurado
                        </span>
                      )}
                    </div>
                  </div>

                  {/* DERECHA: Derechos */}
                  <div className="flex flex-col items-center md:items-end gap-2 text-center md:text-right">
                    <div className="text-xs opacity-80 text-white">
                      {frontpageConfig.footerCopyrightLink ? (
                        <a
                          href={frontpageConfig.footerCopyrightLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-normal text-white hover:font-bold hover:no-underline whitespace-pre-wrap transition-all block"
                        >
                          {frontpageConfig.footerCopyrightText || `© ${new Date().getFullYear()} FarmaTurno Tandil\nTodos los derechos reservados.`}
                        </a>
                      ) : (
                        <p className="font-normal text-white whitespace-pre-wrap block">
                          {frontpageConfig.footerCopyrightText || `© ${new Date().getFullYear()} FarmaTurno Tandil\nTodos los derechos reservados.`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </footer>

              {!loading && frontpageConfig.showWhatsAppFloatingButton && (
                <a
                  href={globalWaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fixed bottom-6 right-6 w-[60px] h-[60px] bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all z-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
                  aria-label="Contactar por WhatsApp"
                >
                  <svg
                    className="w-8 h-8"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.878-.788-1.47-1.761-1.643-2.061-.173-.298-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                  </svg>
                </a>
              )}
            </div>
          );
        })()}

      {/* Standard Administration dashboard layout */}
      {activeTab !== "public" &&
        (() => {
          const isSuperAdmin =
            currentUser?.email?.toLowerCase() === "dataly.com.ar@gmail.com";
          const isShiftAdmin = !!(
            currentUser?.email &&
            frontpageConfig?.adminEmail &&
            currentUser.email.toLowerCase() ===
              frontpageConfig.adminEmail.toLowerCase()
          );
          const isAuthorizedAdmin = isSuperAdmin || isShiftAdmin;

          if (!currentUser) {
            return (
              <div className="min-h-screen bg-slate-900 flex flex-col justify-center py-12 px-6 lg:px-8 font-sans">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                  <div className="flex justify-center flex-col items-center">
                    <span className="text-4xl">🏥</span>
                    <h2 className="mt-4 text-center text-3xl font-extrabold text-white tracking-tight">
                      FarmaTurno Tandil
                    </h2>
                    <p className="mt-2 text-center text-sm text-slate-400">
                      Panel de Gestión para Administradores de Cronograma
                    </p>
                  </div>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                  <div className="bg-slate-800 py-8 px-6 shadow-xl rounded-2xl border border-slate-700 space-y-6">
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-4 text-center">
                        Iniciar Sesión con Google
                      </h3>

                      <div className="bg-white p-3 rounded-xl flex items-center justify-center shadow-sm">
                        <button
                          onClick={async () => {
                            try {
                              const safeAuth = getSafeAuth();
                              if (!safeAuth)
                                throw new Error(
                                  "Firebase Auth is blocked in this environment (sandboxed/third-party).",
                                );
                              const provider = new GoogleAuthProvider();
                              const result = await signInWithPopup(
                                safeAuth,
                                provider,
                              );
                              const user = result.user;
                              if (user && user.email) {
                                setCurrentUser({
                                  email: user.email,
                                  name:
                                    user.displayName ||
                                    user.email.split("@")[0],
                                  picture: user.photoURL || undefined,
                                });
                              }
                            } catch (err: any) {
                              console.warn("Firebase Auth error:", err);
                              const isUnauthorizedDomain =
                                err &&
                                (err.code === "auth/unauthorized-domain" ||
                                  (err.message &&
                                    err.message
                                      .toLowerCase()
                                      .includes("unauthorized-domain")));

                              if (isUnauthorizedDomain) {
                                alert(
                                  "⚠️ Error de Firebase: El dominio '" +
                                    window.location.hostname +
                                    "' no está registrado en los 'Dominios Autorizados' de Firebase Authentication.\n\nPor favor, ingresa a la consola de Firebase de tu proyecto (" +
                                    firebaseConfig.projectId +
                                    ") -> Authentication -> Settings -> Authorized Domains y agrégalo.",
                                );
                              }

                              const email = prompt(
                                "El login por popup está bloqueado o restringido en el navegador de prueba (o falta configurar el dominio en Firebase). Ingrese su dirección de correo de Google para ingresar manualmente:",
                              );
                              if (email) {
                                setCurrentUser({
                                  email: email.trim(),
                                  name: email.split("@")[0],
                                  picture:
                                    "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80",
                                });
                              }
                            }
                          }}
                          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-slate-300 rounded-lg text-slate-700 bg-white hover:bg-slate-50 font-medium text-xs transition duration-150 cursor-pointer"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              fill="#4285F4"
                            />
                            <path
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              fill="#34A853"
                            />
                            <path
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              fill="#FBBC05"
                            />
                            <path
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              fill="#EA4335"
                            />
                          </svg>
                          Ingresar con Google Administrador
                        </button>
                      </div>
                      <p className="mt-3 text-[11px] text-slate-400 text-center">
                        Será validado contra la base de datos de administradores
                        autorizados.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          if (!isAuthorizedAdmin) {
            return (
              <div className="min-h-screen bg-slate-900 flex flex-col justify-center py-12 px-6 lg:px-8 font-sans">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                  <div className="bg-slate-800 py-8 px-6 shadow-xl rounded-2xl border border-slate-700 text-center space-y-6">
                    <div>
                      <span className="text-5xl">🚫</span>
                      <h2 className="mt-4 text-xl font-extrabold text-white">
                        Acceso No Autorizado
                      </h2>
                      <p className="mt-2 text-xs text-red-400 leading-relaxed">
                        El correo{" "}
                        <strong className="font-mono text-white">
                          {currentUser.email}
                        </strong>{" "}
                        no tiene permisos para acceder al sistema.
                      </p>
                    </div>

                    <div className="bg-slate-900/60 p-4 rounded-xl text-left text-xs text-slate-300 border border-slate-700/80">
                      <p className="font-semibold text-slate-200 mb-1">
                        ¿Cómo solucionar esto?
                      </p>
                      <p className="leading-relaxed">
                        El Superadmin debe configurar tu dirección de correo en
                        la sección de control para asignarte autorizaciones de
                        gestión de turnos.
                      </p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => {
                          getSafeAuth()?.signOut().catch(() => {});
                          setCurrentUser(null);
                        }}
                        className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 font-bold text-xs text-white rounded-lg transition duration-150 cursor-pointer"
                      >
                        Iniciar Sesión con Otra Cuenta
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab("public");
                        }}
                        className="w-full px-4 py-2.5 bg-slate-700 hover:bg-slate-650 text-xs font-medium text-slate-200 rounded-lg transition duration-150 cursor-pointer"
                      >
                        Volver a la Vista Pública
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <>
              {/* Connection Board - admin only */}
              <div className="border-b border-slate-200 bg-slate-150 px-4 py-2 text-xs">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      Usuario:{" "}
                      <strong className="text-slate-800 font-mono">
                        {currentUser.email}
                      </strong>
                      <span className="px-1.5 py-0.2 text-[10px] bg-emerald-100 text-emerald-800 rounded font-bold uppercase">
                        {isSuperAdmin ? "Superadmin" : "Admin"}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-slate-600">
                      Hora local Tandil:{" "}
                      <strong className="text-slate-800 font-mono">
                        {currentClockTime || "--:--:--"}
                      </strong>
                    </span>
                    <button
                      onClick={() => {
                        getSafeAuth()?.signOut().catch(() => {});
                        setCurrentUser(null);
                        setActiveTab("public");
                      }}
                      className="text-xs font-bold text-red-600 hover:text-red-500 hover:underline transition"
                    >
                      Salir
                    </button>
                  </div>
                </div>
              </div>

              <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Navigation & Brand Header */}
                <header className="mb-6 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-500 p-2 rounded-xl shadow-sm text-white font-bold">
                      <CalendarIcon size={22} />
                    </div>
                  </div>

                  {/* Tab buttons & Sync */}
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    {/* Quick Link to Public Web */}
                    <button
                      id="btn-tab-public"
                      onClick={() => {
                        setActiveTab("public");
                        setSelectedDate(null);
                      }}
                      className="flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-extrabold rounded-xl transition-all bg-slate-900 border border-slate-800 text-white hover:bg-slate-800 shadow-sm"
                      title="Volver a la vista del vecino de Tandil"
                    >
                      <ExternalLink size={13} />
                      Ver Web Pública
                    </button>

                    <div className="bg-slate-200/60 p-1 rounded-xl border border-slate-200 flex flex-wrap gap-1 w-full sm:w-auto">
                      <button
                        id="btn-tab-calendar"
                        onClick={() => {
                          setActiveTab("calendar");
                          setSelectedDate(null);
                        }}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                          activeTab === "calendar"
                            ? "bg-emerald-500 text-white shadow-sm"
                            : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                        }`}
                      >
                        <CalendarIcon size={13} />
                        Turnos
                      </button>
                      <button
                        id="btn-tab-catalog"
                        onClick={() => {
                          setActiveTab("catalog");
                          setSelectedDate(null);
                        }}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                          activeTab === "catalog"
                            ? "bg-emerald-500 text-white shadow-sm"
                            : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                        }`}
                      >
                        <Database size={13} />
                        Farmacias
                      </button>
                      {isSuperAdmin && (
                        <>
                          <button
                            id="btn-tab-whatsapp"
                            onClick={() => {
                              setActiveTab("whatsapp");
                              setSelectedDate(null);
                            }}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                              activeTab === "whatsapp"
                                ? "bg-emerald-500 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                            }`}
                          >
                            <MessageSquare size={13} />
                            Bot WhatsApp
                          </button>
                          <button
                            id="btn-tab-customize"
                            onClick={() => {
                              setActiveTab("customize");
                              setSelectedDate(null);
                            }}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                              activeTab === "customize"
                                ? "bg-emerald-500 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                            }`}
                          >
                            <Settings size={13} />
                            Personalizar
                          </button>
                          
                          <button
                            id="btn-tab-pdf"
                            onClick={() => {
                              setActiveTab("pdf");
                              setSelectedDate(null);
                            }}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                              activeTab === "pdf"
                                ? "bg-emerald-500 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                            }`}
                          >
                            <FileText size={13} />
                            Carteles
                          </button>
                          <button
                            id="btn-tab-inbox"
                            onClick={() => {
                              setActiveTab("inbox");
                              setSelectedDate(null);
                            }}
                            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                              activeTab === "inbox"
                                ? "bg-emerald-500 text-white shadow-sm"
                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                            }`}
                          >
                            <Mail size={13} />
                            Correos
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowOriginalExcelUploader(true)}
                        className="p-2 px-3 bg-white border border-emerald-200 rounded-xl hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-emerald-600 shadow-sm text-xs font-bold flex items-center gap-1"
                        title="Subir archivo original"
                      >
                        <FileSpreadsheet size={14} /> Subir Original
                      </button>

                      <button
                        onClick={async () => {
                          try {
                            const res = await adminFetch("/api/cache/invalidate", {
                              method: "POST",
                            });
                            if (!res.ok) {
                              const data = await res.json().catch(() => ({}));
                              throw new Error(data.error || `HTTP ${res.status}`);
                            }
                            alert(
                              "Caché regenerada desde la base de datos. El sitio y el bot usarán los datos nuevos en menos de un minuto.",
                            );
                          } catch (e: any) {
                            alert("Error regenerando la caché: " + (e?.message || e));
                          }
                        }}
                        className="p-2 px-3 bg-white border border-slate-200 rounded-xl hover:bg-amber-50 hover:border-amber-300 transition-colors text-amber-600 shadow-sm text-xs font-bold flex items-center gap-1"
                        title="Invalidar caché pública manualmente"
                      >
                        <RefreshCw size={14} /> Regenerar Caché
                      </button>

                      <button
                        onClick={fetchData}
                        disabled={refreshing}
                        className="p-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-colors text-emerald-600 shadow-sm"
                        title="Sincronizar información (desde la base de datos)"
                      >
                        <RefreshCw
                          size={18}
                          className={refreshing ? "animate-spin" : ""}
                        />
                      </button>
                    </div>
                  </div>
                </header>

                {/* Outer Dashboard layout split */}
                <div className="w-full">
                  {/* Main Workspace */}
                  <div className="w-full space-y-6">
                    {/* 1. CALENDAR VIEW ACTIVE */}
                    {activeTab === "calendar" && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-6 overflow-hidden relative shadow-sm">
                        {/* Month navigation and settings info row */}
                        <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200">
                              <button
                                onClick={() => {
                                  handlePrevMonth();
                                  setClearingMonthConfirm(false);
                                }}
                                className="p-1.5 hover:bg-white hover:shadow-xs border border-transparent rounded-lg text-slate-600 transition-all cursor-pointer"
                              >
                                <ChevronLeft size={16} />
                              </button>
                              <h2 className="text-sm font-bold text-slate-800 min-w-[130px] text-center font-sans">
                                {monthNames[currentMonth - 1]} {currentYear}
                              </h2>
                              <button
                                onClick={() => {
                                  handleNextMonth();
                                  setClearingMonthConfirm(false);
                                }}
                                className="p-1.5 hover:bg-white hover:shadow-xs border border-transparent rounded-lg text-slate-600 transition-all cursor-pointer"
                              >
                                <ChevronRight size={16} />
                              </button>
                            </div>

                            {/* Dynamic Safe In-line Clear Month confirmation flow */}
                            {!clearingMonthConfirm ? (
                              <button
                                onClick={() => setClearingMonthConfirm(true)}
                                className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3 py-1.5 border border-rose-200 hover:border-rose-300 bg-rose-50/80 hover:bg-rose-100/80 text-rose-700 text-xs font-bold rounded-xl transition-all cursor-pointer shadow-xs"
                                title="Borrar todos los turnos programados en este mes"
                              >
                                <Trash2 size={12} />
                                Limpiar Mes
                              </button>
                            ) : (
                              <div className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-xl text-xs text-rose-800 font-bold">
                                <span>
                                  ¿Borrar todo {monthNames[currentMonth - 1]}?
                                </span>
                                <div className="flex gap-1.5">
                                  <button
                                    onClick={() =>
                                      handleClearMonth(
                                        currentYear,
                                        currentMonth,
                                      )
                                    }
                                    className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-[10px] font-extrabold transition-colors cursor-pointer"
                                  >
                                    Sí, borrar todo
                                  </button>
                                  <button
                                    onClick={() =>
                                      setClearingMonthConfirm(false)
                                    }
                                    className="px-2 py-1 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-lg text-[10px] font-extrabold transition-colors cursor-pointer"
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="text-xs text-slate-600 flex items-center justify-between sm:justify-center gap-4 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                            <span className="flex items-center gap-1">
                              <Clock size={12} className="text-emerald-500" />
                              Corte diario:{" "}
                              <strong className="text-slate-800">
                                08:30 AM
                              </strong>
                            </span>
                            <span className="text-slate-300">|</span>
                            <span>
                              Farmacias:{" "}
                              <strong className="text-emerald-600">
                                {pharmacies.length}
                              </strong>
                            </span>
                          </div>
                        </div>

                        {/* Calendar grid wrapper */}
                        <div className="grid grid-cols-7 gap-1 text-center font-bold text-xs text-slate-450 border-b border-slate-100 pb-2 mb-2">
                          <div>LUN</div>
                          <div>MAR</div>
                          <div>MIÉ</div>
                          <div>JUE</div>
                          <div>VIE</div>
                          <div>SÁB</div>
                          <div>DOM</div>
                        </div>

                        {/* Calendar Days grids */}
                        <div className="grid grid-cols-7 gap-1">
                          {/* Whitespace buffers */}
                          {Array.from({ length: firstDayOffset }).map(
                            (_, index) => (
                              <div
                                key={`empty-${index}`}
                                className="aspect-video sm:aspect-square bg-slate-100 rounded-lg opacity-40"
                              ></div>
                            ),
                          )}

                          {/* Day blocks */}
                          {Array.from({ length: daysInMonth }).map(
                            (_, index) => {
                              const dayNum = index + 1;
                              const dateStr = `${currentYear}-${String(currentMonth).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
                              const shift = shifts.find(
                                (s) => s.date === dateStr,
                              );
                              const shiftPharms = shift
                                ? pharmacies.filter((p) =>
                                    shift.pharmacyIds.includes(p.id),
                                  )
                                : [];
                              const isSelected = selectedDate === dateStr;

                              return (
                                <div
                                  key={`day-${dayNum}`}
                                  onClick={() => setSelectedDate(dateStr)}
                                  className={`group min-h-[70px] sm:min-h-[110px] p-2 rounded-xl text-left border cursor-pointer select-none transition-all duration-200 flex flex-col justify-between ${
                                    isSelected
                                      ? "bg-gradient-to-tr from-emerald-50 to-slate-50 border-emerald-500 shadow-sm shadow-emerald-100"
                                      : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                                  }`}
                                >
                                  <div className="flex justify-between items-center">
                                    <span
                                      className={`text-xs font-bold font-mono px-2 py-0.5 rounded-lg ${
                                        isSelected
                                          ? "bg-emerald-500 text-white shadow-xs"
                                          : "text-slate-600 bg-slate-100"
                                      }`}
                                    >
                                      {dayNum}
                                    </span>

                                    {/* edit indicator */}
                                    <Edit2
                                      size={10}
                                      className="text-slate-350 opacity-0 group-hover:opacity-100 transition-opacity"
                                    />
                                  </div>

                                  {/* Badges container */}
                                  <div className="mt-2 space-y-1 overflow-hidden flex-1 flex flex-col justify-end">
                                    {shiftPharms.length > 0 ? (
                                      shiftPharms.map((pharm) => (
                                        <div
                                          key={pharm.id}
                                          className="text-[10px] font-semibold truncate bg-emerald-50/80 text-emerald-800 px-1.5 py-0.5 rounded border border-emerald-100 notranslate" translate="no"
                                          title={`${pharm.nombre} (${pharm.direccion})`}
                                        >
                                          {pharm.nombre}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-[9px] text-slate-400 italic">
                                        Sin turnos
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}

                    {/* 2. PHARMACIES CATALOG ACTIVE */}
                    {activeTab === "catalog" && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
                        {/* Title & Filter Header */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-150 pb-4">
                          <div>
                            <h2 className="text-lg font-bold text-slate-800">
                              Catálogo Histórico de Farmacias
                            </h2>
                            <p className="text-xs text-slate-500">
                              Inspecciona, modifica, registra o borra farmacias
                              del maestro principal de Tandil.
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setEditPharmacyMode(true);
                              setCatalogForm({
                                nombre: "",
                                direccion: "",
                                telefono: "",
                                locationUrl: "",
                                fotoUrl: "",
                                colorHex: "#10b981",
                                whatsapp: "",
                              });
                            }}
                            className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 text-xs font-bold rounded-xl hover:bg-emerald-600 transition-all shadow-xs"
                          >
                            <Plus size={14} />
                            Agregar Nueva Farmacia
                          </button>
                        </div>

                        {/* Search query box */}
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-3.5 text-slate-400"
                            size={16}
                          />
                          <input
                            type="text"
                            placeholder="Filtrar farmacias por nombre o dirección..."
                            value={catalogSearch}
                            onChange={(e) => setCatalogSearch(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-10 pr-4 text-xs text-slate-800 focus:outline-none focus:border-emerald-500 font-medium"
                          />
                        </div>

                        {/* Pharmacies List */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {pharmacies
                            .filter((p) => {
                              const query = catalogSearch.toLowerCase();
                              return (
                                p.nombre.toLowerCase().includes(query) ||
                                p.direccion.toLowerCase().includes(query)
                              );
                            })
                            .sort((a, b) => a.nombre.localeCompare(b.nombre))
                            .map((pharm) => {
                              const isDraggingOverThis =
                                draggingOverPharmId === pharm.id;
                              return (
                                <div
                                  key={pharm.id}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    setDraggingOverPharmId(pharm.id);
                                  }}
                                  onDragLeave={() =>
                                    setDraggingOverPharmId(null)
                                  }
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    setDraggingOverPharmId(null);
                                    const file = e.dataTransfer.files?.[0];
                                    if (file) {
                                      handlePharmacyPhotoDrop(pharm, file);
                                    }
                                  }}
                                  style={
                                    pharm.fotoUrl
                                      ? {
                                          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.91), rgba(255, 255, 255, 0.91)), url(${pharm.fotoUrl})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                        }
                                      : undefined
                                  }
                                  className={`relative border p-4 rounded-xl flex justify-between items-start transition-all gap-4 min-h-[110px] select-none ${
                                    isDraggingOverThis
                                      ? "border-emerald-500 bg-emerald-50/80 scale-[1.03] shadow-md ring-2 ring-emerald-300"
                                      : "bg-slate-50/60 border-slate-200 hover:border-slate-350 hover:bg-white shadow-xs"
                                  }`}
                                >
                                  {/* Beautiful overlay indicating drop is active */}
                                  {isDraggingOverThis && (
                                    <div className="absolute inset-0 bg-emerald-500/10 rounded-xl flex flex-col items-center justify-center text-emerald-700 pointer-events-none z-10">
                                      <span className="text-xs font-bold font-mono tracking-wider uppercase bg-white border border-emerald-200 px-3 py-1.5 rounded-full shadow-sm">
                                        📥 Soltar foto aquí
                                      </span>
                                    </div>
                                  )}

                                  {/* Pending confirmation overlay */}
                                  {pendingPhotoUpload?.pharm.id ===
                                    pharm.id && (
                                    <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center text-slate-800 z-20 shadow-lg border border-slate-200">
                                      <p className="text-[11px] font-bold text-center px-4 mb-2 text-slate-700 leading-tight">
                                        ¿Actualizar portada de <br />
                                        <span className="text-emerald-600 text-xs notranslate" translate="no">
                                          {pharm.nombre}
                                        </span>
                                        ?
                                      </p>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() =>
                                            setPendingPhotoUpload(null)
                                          }
                                          className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-bold bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
                                        >
                                          Cancelar
                                        </button>
                                        <button
                                          onClick={() =>
                                            confirmPharmacyPhotoUpload()
                                          }
                                          className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-bold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 shadow-sm transition-colors"
                                        >
                                          Aceptar
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  <div className="min-w-0 z-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <h3 className="font-extrabold text-slate-800 text-sm flex items-center gap-1.5 uppercase tracking-wide notranslate" translate="no">
                                        <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                        {pharm.nombre}
                                      </h3>

                                      {pharm.fotoUrl && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-50 border border-emerald-200 text-emerald-600 font-mono tracking-wider">
                                          📷 Con Foto
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 font-medium">
                                      <MapPin
                                        size={12}
                                        className="text-slate-400 shrink-0"
                                      />
                                      {pharm.direccion}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 font-medium">
                                      <Phone
                                        size={12}
                                        className="text-slate-400 shrink-0"
                                      />
                                      {pharm.telefono}
                                    </p>
                                  </div>

                                  <div className="flex gap-1 z-0 shrink-0">
                                    <button
                                      onClick={() => {
                                        setCatalogForm({
                                          id: pharm.id,
                                          nombre: pharm.nombre,
                                          direccion: pharm.direccion,
                                          telefono: pharm.telefono,
                                          locationUrl: pharm.locationUrl,
                                          fotoUrl: pharm.fotoUrl || "",
                                          colorHex: pharm.colorHex || "#10b981",
                                          whatsapp: pharm.whatsapp || "",
                                        });
                                        setEditPharmacyMode(true);
                                      }}
                                      className="p-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-emerald-600 rounded-lg transition-colors shadow-xs cursor-pointer"
                                      title="Editar"
                                    >
                                      <Settings size={14} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeletePharmacy(pharm.id)
                                      }
                                      className="p-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-500 hover:text-red-500 rounded-lg transition-colors shadow-xs cursor-pointer"
                                      title="Borrar"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* 3. WHATSAPP LOGS LIVE AUDIT VIEW */}
                    {activeTab === "whatsapp" && (
                      <div className="space-y-6 text-slate-900">
                        {/* Header Welcome banner / Superadmin controls */}
                        <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-md">
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                              <span className="bg-emerald-500 text-slate-950 text-[10px] uppercase font-black tracking-widest px-2.5 py-1 rounded-full font-mono">
                                Panel de Superadmin
                              </span>
                              <h2 className="text-xl font-black mt-2 tracking-wide text-slate-100 uppercase">
                                Diagnóstico y Pruebas del Bot de WhatsApp
                              </h2>
                              <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                                Control de credenciales de Meta Developer,
                                simulación de webhook en tiempo real y flujo de
                                logs para auditoría.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                          {/* Left Column (2/3 width) - The Traffic Logs */}
                          <div className="xl:col-span-2 space-y-6">
                            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
                              {/* Header */}
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-150 pb-4">
                                <div>
                                  <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                    <MessageSquare
                                      size={16}
                                      className="text-emerald-500 font-bold"
                                    />
                                    Historial de Tráfico de Mensajes
                                  </h2>
                                  <p className="text-[11px] text-slate-450">
                                    Observación detallada de handshakes,
                                    entregas, recepciones, simulaciones y
                                    errores en tiempo real.
                                  </p>
                                </div>

                                <div className="flex items-center gap-2">
                                  <label className="flex items-center gap-1 text-[10px] font-bold text-slate-600 select-none cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={hideBotLogs}
                                      onChange={(e) =>
                                        setHideBotLogs(e.target.checked)
                                      }
                                    />
                                    Ocultar mensajes del bot
                                  </label>
                                  <span className="text-[10px] px-2.2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-mono">
                                    Logs en memoria:{" "}
                                    {diagData?.logs?.length || 0}
                                  </span>
                                  {diagData?.logs &&
                                    diagData.logs.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={handleClearLogs}
                                          disabled={clearingLogs}
                                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
                                            showClearConfirm
                                              ? "bg-rose-600 text-white hover:bg-rose-700 animate-pulse"
                                              : "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-100"
                                          }`}
                                          title={
                                            showClearConfirm
                                              ? "Haz clic de nuevo para CONFIRMAR"
                                              : "Vaciar todos los logs"
                                          }
                                        >
                                          <Trash2
                                            size={11}
                                            className={`font-bold ${showClearConfirm ? "animate-bounce" : ""}`}
                                          />
                                          {clearingLogs
                                            ? "Limpiando..."
                                            : showClearConfirm
                                              ? "⚠️ Confirmar Vaciar"
                                              : "Vaciar Logs"}
                                        </button>
                                        {showClearConfirm && (
                                          <button
                                            onClick={() =>
                                              setShowClearConfirm(false)
                                            }
                                            className="text-[9.5px] font-medium text-slate-500 hover:text-slate-800 underline px-1 cursor-pointer"
                                            title="Cancelar acción"
                                          >
                                            Cancelar
                                          </button>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>

                              {/* Console Log Panel */}
                              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/50 mb-0">
                                <table className="w-full text-left text-xs border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100 border-b border-slate-200 text-slate-550 uppercase font-mono tracking-wider">
                                      <th className="p-3 font-semibold">
                                        Hora
                                      </th>
                                      <th className="p-3 font-semibold">
                                        Dirección
                                      </th>
                                      <th className="p-3 font-semibold">
                                        Número
                                      </th>
                                      <th className="p-3 font-semibold">
                                        Mensaje
                                      </th>
                                      <th className="p-3 font-semibold">
                                        Estado
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {visibleLogs.length > 0 ? (
                                      visibleLogs.map((log, index) => {
                                        const isSent =
                                          log.direction === "outbound";
                                        const hasMetaError =
                                          log.status === "error";

                                        return (
                                          <tr
                                            key={index}
                                            className="hover:bg-slate-100/50 transition-colors font-medium text-slate-800"
                                          >
                                            <td className="p-3 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                                              {new Date(
                                                log.timestamp,
                                              ).toLocaleTimeString("es-AR", {
                                                timeZone:
                                                  "America/Argentina/Buenos_Aires",
                                                hour12: false,
                                              })}
                                            </td>
                                            <td className="p-3 whitespace-nowrap">
                                              <span
                                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${
                                                  isSent
                                                    ? "bg-amber-50 text-amber-700 border border-amber-100"
                                                    : "bg-sky-50 text-sky-700 border border-sky-100"
                                                }`}
                                              >
                                                {isSent
                                                  ? "Saliente"
                                                  : "Entrante"}
                                              </span>
                                            </td>
                                            <td className="p-3 font-mono text-slate-700 whitespace-nowrap">
                                              {isSent ? log.to : log.from}
                                            </td>
                                            <td className="p-3 text-slate-700 font-sans max-w-[250px] overflow-hidden truncate whitespace-pre-wrap leading-relaxed">
                                              {log.body}
                                            </td>
                                            <td className="p-3">
                                              <div className="space-y-1.5">
                                                <span
                                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    log.status ===
                                                      "delivered" ||
                                                    log.status === "received"
                                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                                      : log.status ===
                                                          "simulated"
                                                        ? "bg-purple-50 text-purple-700 border border-purple-100"
                                                        : "bg-rose-50 text-rose-750 border border-rose-100 animate-pulse"
                                                  }`}
                                                >
                                                  {log.status === "error"
                                                    ? "Meta Error ⚠️"
                                                    : log.status}
                                                </span>
                                                {hasMetaError && log.error && (
                                                  <div className="text-[10px] text-rose-700 bg-rose-50 p-2 rounded border border-rose-100 max-w-[200px] leading-relaxed">
                                                    {log.error}
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    ) : (
                                      <tr>
                                        <td
                                          colSpan={5}
                                          className="p-8 text-center text-slate-400 italic font-medium"
                                        >
                                          {hideBotLogs && diagData?.logs?.length
                                            ? "No hay mensajes entrantes. Los mensajes del bot están ocultos."
                                            : "No se registran interacciones en el monitor de comunicación aún."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          {/* Right Column (1/3 width) - Webhook & Simulator */}
                          <div className="space-y-6">
                            <BotMessageEditor />

                            {/* 2. CHATBOT TESTER & SIMULATOR */}
                            <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 text-slate-900">
                              <div className="flex items-center gap-2 border-b border-slate-150 pb-3">
                                <MessageSquare
                                  size={16}
                                  className="text-emerald-500"
                                />
                                <h2 className="text-sm font-bold text-slate-800 font-sans">
                                  Probador de Bot de Turnos
                                </h2>
                              </div>
                              <p className="text-[10px] text-slate-550 leading-relaxed font-medium">
                                Simula recibir palabras clave en el backend para
                                ver qué respondería el bot de turno de Tandil.
                              </p>

                              <div className="space-y-3">
                                <div>
                                  <label className="block text-[10px] font-extrabold uppercase text-slate-400 mb-1 font-mono">
                                    Número Celular
                                  </label>
                                  <input
                                    type="text"
                                    value={simPhone}
                                    onChange={(e) =>
                                      setSimPhone(e.target.value)
                                    }
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-mono focus:outline-none focus:border-emerald-500"
                                  />
                                </div>

                                <div>
                                  <label className="block text-[10px] font-extrabold uppercase text-slate-400 mb-1 font-mono">
                                    Simular Mensaje
                                  </label>
                                  <div className="flex gap-1.5 flex-wrap">
                                    {["hola", "hoy", "mañana", "soporte"].map(
                                      (word) => (
                                        <button
                                          key={word}
                                          onClick={() => setSimText(word)}
                                          className={`text-[10px] px-2 py-1 rounded border capitalize transition-colors cursor-pointer ${
                                            simText === word
                                              ? "bg-emerald-50 border-emerald-500 text-emerald-700 font-bold shadow-xs"
                                              : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                                          }`}
                                        >
                                          {word}
                                        </button>
                                      ),
                                    )}
                                  </div>
                                  <input
                                    type="text"
                                    value={simText}
                                    onChange={(e) => setSimText(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-850 font-medium mt-2 focus:outline-none focus:border-emerald-500"
                                    placeholder="Escribe pregunta ej: ¿quien esta de turno hoy?"
                                  />
                                </div>

                                <button
                                  onClick={handleSimulateMsg}
                                  disabled={simStatus?.loading}
                                  className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                                >
                                  <Send size={12} />
                                  Simular Recibir Webhook
                                </button>

                                {simStatus?.text && (
                                  <div className="text-[10px] font-bold text-emerald-700 bg-emerald-50 p-2.5 rounded-lg border border-emerald-100">
                                    {simStatus.text}
                                  </div>
                                )}
                              </div>
                            </section>

                            {/* 3. INTEGRATION META CONFIG CARD */}
                            <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4 text-slate-900">
                              <div className="flex items-center gap-2 border-b border-slate-150 pb-3">
                                <Settings
                                  size={16}
                                  className="text-emerald-500"
                                />
                                <h2 className="text-sm font-bold text-slate-800 font-sans">
                                  Verificador de Integración
                                </h2>
                              </div>

                              {/* Webhook Settings Section (Requested) */}
                              <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                                <h3 className="text-[11px] font-extrabold uppercase text-slate-700 tracking-wide font-mono">
                                  🔗 Recibir Webhook de Meta
                                </h3>
                                <p className="text-[10px] text-slate-500 leading-normal font-sans">
                                  Copia y pega estos datos en la configuración
                                  de Webhooks de Meta para suscribirte a los
                                  mensajes entrantes:
                                </p>

                                <div className="space-y-3 mt-2">
                                  {/* Production Callback URL (Main) */}
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <label className="block text-[9px] font-bold text-indigo-600 uppercase tracking-wider">
                                        URL Webhook Producción (Cloud Run)
                                      </label>
                                      <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[8px] font-extrabold uppercase px-1.5 py-0.2 rounded">
                                        Principal
                                      </span>
                                    </div>
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        readOnly
                                        value="https://tandil-farmacias-de-turno-782547659324.us-west1.run.app/api/whatsapp"
                                        className="w-full bg-white border border-indigo-200 rounded-lg px-2 py-1 text-[10px] text-indigo-800 font-semibold font-mono focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          copyToClipboard(
                                            "https://tandil-farmacias-de-turno-782547659324.us-west1.run.app/api/whatsapp",
                                            setCopiedProdWebhook
                                          );
                                        }}
                                        className={`px-2.5 py-1 border rounded-lg flex items-center justify-center gap-1 text-[10px] font-bold cursor-pointer transition-all ${
                                          copiedProdWebhook
                                            ? "bg-emerald-50 border-emerald-300 text-emerald-600"
                                            : "bg-white hover:bg-slate-50 border-slate-250 text-slate-600"
                                        }`}
                                        title="Copiar URL"
                                      >
                                        {copiedProdWebhook ? (
                                          <Check size={11} />
                                        ) : (
                                          <Copy size={11} />
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Sandbox / Dev Callback URL */}
                                  <div>
                                    <div className="flex justify-between items-center mb-1">
                                      <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                                        URL Webhook Desarrollo (AI Studio
                                        Sandbox)
                                      </label>
                                      <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[8px] font-extrabold uppercase px-1.5 py-0.2 rounded">
                                        Pruebas
                                      </span>
                                    </div>
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        readOnly
                                        value={
                                          diagData?.webhookUrl ||
                                          (() => {
                                            const origin =
                                              window.location.origin;
                                            return (
                                              (origin.includes("ais-dev-")
                                                ? origin.replace(
                                                    "ais-dev-",
                                                    "ais-pre-",
                                                  )
                                                : origin) + "/api/whatsapp"
                                            );
                                          })()
                                        }
                                        className="w-full bg-white border border-slate-250 rounded-lg px-2 py-1 text-[10px] text-slate-600 font-mono focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const publicUrl =
                                            diagData?.webhookUrl ||
                                            (() => {
                                              const origin =
                                                window.location.origin;
                                              return (
                                                (origin.includes("ais-dev-")
                                                  ? origin.replace(
                                                      "ais-dev-",
                                                      "ais-pre-",
                                                    )
                                                  : origin) + "/api/whatsapp"
                                              );
                                            })();
                                          copyToClipboard(publicUrl, setCopiedWebhook);
                                        }}
                                        className={`px-2.5 py-1 border rounded-lg flex items-center justify-center gap-1 text-[10px] font-bold cursor-pointer transition-all ${
                                          copiedWebhook
                                            ? "bg-emerald-50 border-emerald-300 text-emerald-600"
                                            : "bg-white hover:bg-slate-50 border-slate-250 text-slate-600"
                                        }`}
                                        title="Copiar URL"
                                      >
                                        {copiedWebhook ? (
                                          <Check size={11} />
                                        ) : (
                                          <Copy size={11} />
                                        )}
                                      </button>
                                    </div>
                                  </div>

                                  {/* Verification Token */}
                                  <div>
                                    <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                      Token de verificación (Verify Token)
                                    </label>
                                    <div className="flex gap-1">
                                      <input
                                        type="text"
                                        readOnly
                                        value={
                                          diagData?.config
                                            ?.WHATSAPP_VERIFY_TOKEN ||
                                          "TandilVerify"
                                        }
                                        className="w-full bg-white border border-slate-250 rounded-lg px-2 py-1 text-[10px] text-slate-700 font-mono focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          copyToClipboard(
                                            diagData?.config
                                              ?.WHATSAPP_VERIFY_TOKEN ||
                                              "TandilVerify",
                                            setCopiedToken
                                          );
                                        }}
                                        className={`px-2.5 py-1 border rounded-lg flex items-center justify-center gap-1 text-[10px] font-bold cursor-pointer transition-all ${
                                          copiedToken
                                            ? "bg-emerald-50 border-emerald-300 text-emerald-600"
                                            : "bg-white hover:bg-slate-50 border-slate-250 text-slate-600"
                                        }`}
                                        title="Copiar Token"
                                      >
                                        {copiedToken ? (
                                          <Check size={11} />
                                        ) : (
                                          <Copy size={11} />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Status metrics display */}
                              <p className="text-[10px] text-slate-550 leading-relaxed font-bold pt-1 uppercase font-mono">
                                Credenciales en Memoria:
                              </p>

                              <div className="space-y-1.5 text-[10px] font-mono">
                                <div className="bg-slate-50 p-2 rounded border border-slate-150 flex justify-between">
                                  <span className="text-slate-500">
                                    PHONE_ID:
                                  </span>
                                  <span className="text-slate-800 font-semibold">
                                    {diagData?.config
                                      ?.WHATSAPP_PHONE_NUMBER_ID ||
                                      "No cargado"}
                                  </span>
                                </div>
                                <div className="bg-slate-50 p-2 rounded border border-slate-150 flex justify-between">
                                  <span className="text-slate-500">
                                    VERIFY:
                                  </span>
                                  <span className="text-slate-800 font-semibold">
                                    {diagData?.config?.WHATSAPP_VERIFY_TOKEN ||
                                      "No cargado"}
                                  </span>
                                </div>
                                <div className="bg-slate-50 p-2 rounded border border-slate-150 flex flex-col gap-1">
                                  <span className="text-slate-500">
                                    ACCESS_TOKEN:
                                  </span>
                                  <span className="text-slate-700 truncate">
                                    {diagData?.config
                                      ?.WHATSAPP_ACCESS_TOKEN_MASKED ||
                                      "No cargado"}
                                  </span>
                                </div>
                              </div>

                              <div className="text-[10px] text-slate-500 leading-relaxed bg-slate-100 p-2.5 rounded-lg border border-slate-200">
                                ⚠️ Si falta configurar, las respuestas se
                                loguearán en modo simulado para usar el webhook
                                localmente sin interrupciones.
                              </div>

                              <button
                                onClick={handleOpenConfig}
                                className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
                              >
                                <Settings size={12} />
                                Editar Configuración de Meta
                              </button>
                            </section>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "pdf" && (
                      <PDFGenerator shifts={shifts} pharmacies={pharmacies} />
                    )}
                    {activeTab === "inbox" && (
                      <EmailInbox />
                    )}

                    {/* 4. FRONT-PAGE LANDING CUSTOMIZE VIEW */}
                    {activeTab === "customize" && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
                        <div>
                          <h2 className="text-lg font-bold text-slate-850 flex items-center gap-2">
                            <Settings className="text-emerald-500" size={18} />
                            Diseño & Anuncios de la Portada Pública
                          </h2>
                          <p className="text-xs text-slate-500">
                            Sintoniza los dos banners dinámicos y la apariencia
                            de la cartelera de turnos para los vecinos de
                            Tandil.
                          </p>
                        </div>

                        <form
                          onSubmit={handleSaveCustomize}
                          className="space-y-6"
                        >
                          {/* Banner superior config */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              1. Banner Superior (Altura configurable de ~90px)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Texto del Anuncio
                                </label>
                                <input
                                  type="text"
                                  value={customizeForm.topBannerText}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      topBannerText: e.target.value,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Enlace Web (URL de Redirección)
                                </label>
                                <input
                                  type="text"
                                  value={customizeForm.topBannerLink}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      topBannerLink: e.target.value,
                                    })
                                  }
                                  placeholder="Ej. https://tandil.gov.ar"
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                />
                               </div>
                              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                                <label className="flex items-center gap-2 cursor-pointer select-none bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors flex-1">
                                  <input
                                    type="checkbox"
                                    id="hideTopBannerDesktop"
                                    checked={!!customizeForm.hideTopBannerDesktop}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        hideTopBannerDesktop: e.target.checked,
                                      })
                                    }
                                    className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                  />
                                  <div>
                                    <span className="block text-xs font-bold text-slate-700">
                                      💻 Ocultar en escritorio / PC
                                    </span>
                                    <span className="block text-[10px] text-slate-400">
                                      Oculta en computadoras sin borrar la imagen
                                    </span>
                                  </div>
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer select-none bg-white p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors flex-1">
                                  <input
                                    type="checkbox"
                                    id="hideTopBannerMobile"
                                    checked={!!customizeForm.hideTopBannerMobile}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        hideTopBannerMobile: e.target.checked,
                                      })
                                    }
                                    className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                  />
                                  <div>
                                    <span className="block text-xs font-bold text-slate-700">
                                      📱 Ocultar en celulares / móvil
                                    </span>
                                    <span className="block text-[10px] text-slate-400">
                                      Oculta en teléfonos sin borrar la imagen
                                    </span>
                                  </div>
                                </label>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="md:col-span-2 space-y-4">
                                <div>
                                  <ImageUploaderWidget
                                    label="Foto del Banner Superior (Escritorio / General)"
                                    value={customizeForm.topBannerImage || ""}
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        topBannerImage: url,
                                      })
                                    }
                                    idPrefix="top-banner-desktop"
                                  />
                                  <p className="text-[10px] text-slate-450 mt-1 font-sans">
                                    📐 **Ancho ideal de Escritorio:** **1200px a
                                    1920px** (Ej: `1920x250`). El banner se
                                    adapta a la altura configurada.
                                  </p>
                                  <div className="flex gap-2 items-center mt-1">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                                      O Enlace Web Directo (Escritorio):
                                    </span>
                                    <input
                                      type="text"
                                      value={customizeForm.topBannerImage || ""}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          topBannerImage: e.target.value,
                                        })
                                      }
                                      placeholder="Ej. https://images.unsplash.com/..."
                                      className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <ImageUploaderWidget
                                    label="Foto del Banner Superior (Versión Móvil - Opcional)"
                                    value={
                                      customizeForm.topBannerMobileImage || ""
                                    }
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        topBannerMobileImage: url,
                                      })
                                    }
                                    idPrefix="top-banner-mobile"
                                  />
                                  <p className="text-[10px] text-slate-450 mt-1 font-sans">
                                    📐 **Ancho ideal para Móviles:** **360px a
                                    640px** (Ej: `640x180` o proporción similar
                                    para pantallas táctiles). Ofrece carga veloz
                                    en móviles.
                                  </p>
                                  <div className="flex gap-2 items-center mt-1">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                                      O Enlace Web Directo (Móvil):
                                    </span>
                                    <input
                                      type="text"
                                      value={
                                        customizeForm.topBannerMobileImage || ""
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          topBannerMobileImage: e.target.value,
                                        })
                                      }
                                      placeholder="Ej. https://images.unsplash.com/..."
                                      className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Altura (Pixels)
                                </label>
                                <input
                                  type="number"
                                  value={customizeForm.topBannerHeight}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      topBannerHeight:
                                        parseInt(e.target.value) || 90,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono font-bold focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                Color de Fondo Sólido
                              </label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={
                                    customizeForm.topBannerBgColor || "#0f172a"
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      topBannerBgColor: e.target.value,
                                    })
                                  }
                                  className="h-8 w-10 border-0 bg-transparent cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={customizeForm.topBannerBgColor}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      topBannerBgColor: e.target.value,
                                    })
                                  }
                                  className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Banner inferior/segundo banner config */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              2. Segundo Banner (Banners de Campaña, Anuncios o
                              Imagen)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Mensaje o Anuncio (Si no subes foto)
                                </label>
                                <textarea
                                  rows={2}
                                  value={customizeForm.bottomBannerText || ""}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      bottomBannerText: e.target.value,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Enlace Web (URL de Redirección opcional)
                                </label>
                                <input
                                  type="text"
                                  value={customizeForm.bottomBannerLink || ""}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      bottomBannerLink: e.target.value,
                                    })
                                  }
                                  placeholder="Ej. https://wa.me/549..."
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="md:col-span-2 space-y-4">
                                <div>
                                  <ImageUploaderWidget
                                    label="Foto del Segundo Banner (Escritorio / General)"
                                    value={
                                      customizeForm.bottomBannerImage || ""
                                    }
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        bottomBannerImage: url,
                                      })
                                    }
                                    idPrefix="bottom-banner-desktop"
                                  />
                                  <p className="text-[10px] text-slate-450 mt-1 font-sans">
                                    📐 **Ancho ideal de Escritorio:** **1200px a
                                    1920px** (Ej: `1920x350` o similar). Ideal
                                    para visualizaciones nítidas.
                                  </p>
                                  <div className="flex gap-2 items-center mt-1">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                                      O Enlace Web Directo (Escritorio):
                                    </span>
                                    <input
                                      type="text"
                                      value={
                                        customizeForm.bottomBannerImage || ""
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          bottomBannerImage: e.target.value,
                                        })
                                      }
                                      placeholder="Ej. https://images.unsplash.com/..."
                                      className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                    />
                                  </div>
                                </div>

                                <div>
                                  <ImageUploaderWidget
                                    label="Foto del Segundo Banner (Versión Móvil - Opcional)"
                                    value={
                                      customizeForm.bottomBannerMobileImage ||
                                      ""
                                    }
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        bottomBannerMobileImage: url,
                                      })
                                    }
                                    idPrefix="bottom-banner-mobile"
                                  />
                                  <p className="text-[10px] text-slate-450 mt-1 font-sans">
                                    📐 **Ancho ideal para Móviles:** **360px a
                                    640px** (Ej: `640x320`). Se renderiza
                                    optimizado para displays táctiles y
                                    verticales.
                                  </p>
                                  <div className="flex gap-2 items-center mt-1">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                                      O Enlace Web Directo (Móvil):
                                    </span>
                                    <input
                                      type="text"
                                      value={
                                        customizeForm.bottomBannerMobileImage ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          bottomBannerMobileImage:
                                            e.target.value,
                                        })
                                      }
                                      placeholder="Ej. https://images.unsplash.com/..."
                                      className="flex-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Altura Máxima (Pixels)
                                </label>
                                <input
                                  type="number"
                                  value={
                                    customizeForm.bottomBannerHeight || 170
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      bottomBannerHeight:
                                        parseInt(e.target.value) || 170,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono font-bold focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                Color de Fondo Sólido
                              </label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={
                                    customizeForm.bottomBannerBgColor ||
                                    "#1e293b"
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      bottomBannerBgColor: e.target.value,
                                    })
                                  }
                                  className="h-8 w-10 border-0 bg-transparent cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={
                                    customizeForm.bottomBannerBgColor || ""
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      bottomBannerBgColor: e.target.value,
                                    })
                                  }
                                  className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                            </div>
                          </div>

                          {/* WhatsApp banner toggle and positioning */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              3. Opciones del Banner de WhatsApp Promocional
                            </h3>

                            <div>
                              <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                Texto del Mensaje de WhatsApp Promocional
                              </label>
                              <input
                                type="text"
                                value={customizeForm.whatsAppBannerText || ""}
                                onChange={(e) =>
                                  setCustomizeForm({
                                    ...customizeForm,
                                    whatsAppBannerText: e.target.value,
                                  })
                                }
                                placeholder="Ej. 📲 Consulta quién está de guardia hoy o mañana cómodamente por WhatsApp."
                                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Número de Destino (Consultas)
                                </label>
                                <input
                                  type="text"
                                  value={
                                    customizeForm.whatsAppConsultPhone || ""
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      whatsAppConsultPhone: e.target.value,
                                    })
                                  }
                                  placeholder="Ej. 5492494123456"
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                />
                              </div>
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Texto por Defecto
                                </label>
                                <input
                                  type="text"
                                  value={
                                    customizeForm.whatsAppConsultDefaultText ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      whatsAppConsultDefaultText:
                                        e.target.value,
                                    })
                                  }
                                  placeholder="Ej. Hoy"
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="flex items-start gap-3 pt-1">
                                <input
                                  type="checkbox"
                                  id="showWhatsAppBanner"
                                  checked={!!customizeForm.showWhatsAppBanner}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      showWhatsAppBanner: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                />
                                <div>
                                  <label
                                    htmlFor="showWhatsAppBanner"
                                    className="block text-xs font-bold text-slate-700 cursor-pointer"
                                  >
                                    Habilitar Banner Promocional de WhatsApp
                                  </label>
                                  <p className="text-[10px] text-slate-400 mt-0.5">
                                    Si lo desactivas, no se mostrará esta
                                    campaña.
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-start gap-3 pt-1">
                                <input
                                  type="checkbox"
                                  id="showWhatsAppFloatingButton"
                                  checked={
                                    !!customizeForm.showWhatsAppFloatingButton
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      showWhatsAppFloatingButton:
                                        e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                />
                                <div>
                                  <label
                                    htmlFor="showWhatsAppFloatingButton"
                                    className="block text-xs font-bold text-slate-700 cursor-pointer"
                                  >
                                    Habilitar Botón Flotante de WhatsApp
                                  </label>
                                  <p className="text-[10px] text-slate-400 mt-0.5">
                                    Muestra un botón de contacto persistente en
                                    la esquina inferior.
                                  </p>
                                </div>
                              </div>

                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Posición en la Pantalla
                                </label>
                                <select
                                  value={
                                    customizeForm.whatsAppBannerPosition ||
                                    "top"
                                  }
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      whatsAppBannerPosition: e.target.value,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                >
                                  <option value="top">
                                    Parte Superior (Arriba de la cartelera)
                                  </option>
                                  <option value="bottom">
                                    Parte Inferior (Abajo de la cartelera)
                                  </option>
                                </select>
                                <p className="text-[10px] text-slate-400 mt-1">
                                  Elige si prefieres ubicar esta sección arriba
                                  del cronograma o debajo.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* 4. DESIGN OF PHARMACIES ROW COUNT & CAROUSEL */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              4. Diseño del Listado de Farmacias (Filas &
                              Carrusel)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                  Farmacias por Fila (Escritorio)
                                </label>
                                <select
                                  value={customizeForm.pharmaciesPerRow || 2}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      pharmaciesPerRow:
                                        parseInt(e.target.value) || 2,
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                >
                                  <option value="1">
                                    1 Farmacia por fila (Ancho Completo)
                                  </option>
                                  <option value="2">
                                    2 Farmacias por fila (Recomendado)
                                  </option>
                                  <option value="3">
                                    3 Farmacias por fila
                                  </option>
                                  <option value="4">
                                    4 Farmacias por fila (Compacto)
                                  </option>
                                </select>
                                <p className="text-[10px] text-slate-400 mt-1 font-sans">
                                  Elige cuántas tarjetas de farmacias deseas
                                  mostrar por fila en la pantalla de la
                                  computadora.
                                </p>
                              </div>

                              <div className="flex items-start gap-3 pt-6">
                                <input
                                  type="checkbox"
                                  id="carouselDesktop"
                                  checked={!!customizeForm.carouselDesktop}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      carouselDesktop: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                />
                                <div>
                                  <label
                                    htmlFor="carouselDesktop"
                                    className="block text-xs font-bold text-slate-700 cursor-pointer"
                                  >
                                    Habilitar vista estilo Carrusel / Deslizador
                                    móvil/escritorio
                                  </label>
                                  <p className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                    Muestra las farmacias una al lado de la otra
                                    con flechas de navegación e indicadores de
                                    círculos en la parte inferior.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 5. CONFIGURATION OF PLACA DETAILS BOX */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-5">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              5. Personalización de Placa (Colores, Fotos y
                              Fuentes)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Left Side: Color and photo visibility */}
                              <div className="space-y-4">
                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase">
                                    Color de Fondo de la Placa (Detalles)
                                  </label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={
                                        customizeForm.detailsBoxColor ||
                                        "#10b981"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          detailsBoxColor: e.target.value,
                                        })
                                      }
                                      className="h-10 w-16 border border-slate-200 rounded-lg cursor-pointer bg-white p-1"
                                    />
                                    <input
                                      type="text"
                                      value={
                                        customizeForm.detailsBoxColor ||
                                        "#10b981"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          detailsBoxColor: e.target.value,
                                        })
                                      }
                                      className="w-28 bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-xs font-mono font-semibold uppercase text-slate-800"
                                    />
                                  </div>

                                  {/* Color presets */}
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {[
                                      "#10b981",
                                      "#2563eb",
                                      "#1e293b",
                                      "#0f172a",
                                      "#7c3aed",
                                      "#e11d48",
                                      "#0d9488",
                                    ].map((preset) => (
                                      <button
                                        key={preset}
                                        type="button"
                                        onClick={() =>
                                          setCustomizeForm({
                                            ...customizeForm,
                                            detailsBoxColor: preset,
                                          })
                                        }
                                        style={{ backgroundColor: preset }}
                                        className="h-5 w-5 rounded-full border border-white shadow-xs cursor-pointer active:scale-95 transition-transform"
                                        title={preset}
                                      />
                                    ))}
                                  </div>
                                </div>

                                <div className="flex items-start gap-3 pt-2">
                                  <input
                                    type="checkbox"
                                    id="showPhotoOnMobile"
                                    checked={
                                      customizeForm.showPhotoOnMobile !== false
                                    }
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        showPhotoOnMobile: e.target.checked,
                                      })
                                    }
                                    className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                  />
                                  <div>
                                    <label
                                      htmlFor="showPhotoOnMobile"
                                      className="block text-xs font-bold text-slate-700 cursor-pointer"
                                    >
                                      Mostrar foto de la farmacia en versión
                                      móvil
                                    </label>
                                    <p className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                      Si se desmarca, en celulares se ocultará
                                      el panel de la foto y se mostrará un
                                      listado compacto más limpio con el nombre
                                      arriba.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Right Side: Typography specifications */}
                              <div className="space-y-4 bg-white p-4 rounded-lg border border-slate-200">
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                  Tipografía del Texto de Dirección
                                </span>

                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Tamaño
                                    </label>
                                    <select
                                      value={
                                        customizeForm.addressFontSize || "sm"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          addressFontSize: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="xs">Muy Chico</option>
                                      <option value="sm">Chico</option>
                                      <option value="base">Normal</option>
                                      <option value="lg">Grande</option>
                                      <option value="xl">Muy Grande</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Grosor
                                    </label>
                                    <select
                                      value={
                                        customizeForm.addressFontWeight ||
                                        "semibold"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          addressFontWeight: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="normal">Normal</option>
                                      <option value="medium">Medio</option>
                                      <option value="semibold">
                                        Semi-Negrita
                                      </option>
                                      <option value="bold">Negrita</option>
                                      <option value="extrabold">
                                        Extra-Negrita
                                      </option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Color
                                    </label>
                                    <select
                                      value={
                                        customizeForm.addressFontColor ||
                                        "white"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          addressFontColor: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="white">Blanco</option>
                                      <option value="slate-900">
                                        Gris Oscuro (Slate 900)
                                      </option>
                                      <option value="slate-800">
                                        Gris Medio (Slate 800)
                                      </option>
                                      <option value="emerald-900">
                                        Verde Oscuro
                                      </option>
                                      <option value="yellow-300">
                                        Amarillo Oro
                                      </option>
                                    </select>
                                  </div>
                                </div>

                                <div className="border-t border-slate-100 my-2 pt-2" />

                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                  Tipografía del Texto de Teléfono
                                </span>

                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Tamaño
                                    </label>
                                    <select
                                      value={
                                        customizeForm.phoneFontSize || "sm"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          phoneFontSize: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="xs">Muy Chico</option>
                                      <option value="sm">Chico</option>
                                      <option value="base">Normal</option>
                                      <option value="lg">Grande</option>
                                      <option value="xl">Muy Grande</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Grosor
                                    </label>
                                    <select
                                      value={
                                        customizeForm.phoneFontWeight ||
                                        "semibold"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          phoneFontWeight: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="normal">Normal</option>
                                      <option value="medium">Medio</option>
                                      <option value="semibold">
                                        Semi-Negrita
                                      </option>
                                      <option value="bold">Negrita</option>
                                      <option value="extrabold">
                                        Extra-Negrita
                                      </option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-[8px] text-slate-400 uppercase font-mono font-bold">
                                      Color
                                    </label>
                                    <select
                                      value={
                                        customizeForm.phoneFontColor || "white"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          phoneFontColor: e.target.value,
                                        })
                                      }
                                      className="w-full bg-slate-50 border border-slate-200 rounded px-1.5 py-1 text-[11px] font-semibold focus:outline-none"
                                    >
                                      <option value="white">Blanco</option>
                                      <option value="slate-900">
                                        Gris Oscuro (Slate 900)
                                      </option>
                                      <option value="slate-800">
                                        Gris Medio (Slate 800)
                                      </option>
                                      <option value="emerald-900">
                                        Verde Oscuro
                                      </option>
                                      <option value="yellow-300">
                                        Amarillo Oro
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 6. CONFIGURATION OF COUNTDOWN TIMER CARD */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              6. Ajustes de Cuenta Regresiva (Home Box)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  id="hideCountdownFrame"
                                  checked={!!customizeForm.hideCountdownFrame}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      hideCountdownFrame: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                />
                                <div>
                                  <label
                                    htmlFor="hideCountdownFrame"
                                    className="block text-xs font-bold text-slate-700 cursor-pointer"
                                  >
                                    Quitar recuadro y fondo (Hacer transparente)
                                  </label>
                                  <p className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                    Elimina bordes, sombras y el fondo blanco
                                    del bloque de la cuenta regresiva.
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  id="countdownBelowToggle"
                                  checked={!!customizeForm.countdownBelowToggle}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      countdownBelowToggle: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 mt-1 cursor-pointer"
                                />
                                <div>
                                  <label
                                    htmlFor="countdownBelowToggle"
                                    className="block text-xs font-bold text-slate-700 cursor-pointer"
                                  >
                                    Ubicar debajo del botón "Ver farmacias del
                                    siguiente turno"
                                  </label>
                                  <p className="text-[10px] text-slate-400 mt-0.5 font-sans">
                                    Mueve el bloque de cuenta regresiva para que
                                    se renderice debajo de los botones de cambio
                                    de turno.
                                  </p>
                                </div>
                              </div>

                              {/* 🧭 Posición del bloque de Geolocalización */}
                              <div className="flex flex-col gap-1 border-t border-slate-200/60 pt-4 md:col-span-2">
                                <label className="block text-xs font-extrabold text-slate-700 font-sans uppercase tracking-wider text-[11px]">
                                  🧭 Posición del recuadro de Geolocalización
                                  ("¿Cuál me queda más cerca?")
                                </label>
                                <p className="text-[10px] text-slate-400 mb-2 font-sans">
                                  Elige si el buscador de distancias y
                                  ordenamiento por cercanía debe mostrarse
                                  arriba de las farmacias de turno o debajo.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer select-none">
                                    <input
                                      type="radio"
                                      name="locationBoxPosition"
                                      value="above"
                                      checked={
                                        customizeForm.locationBoxPosition ===
                                        "above"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          locationBoxPosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                    />
                                    Arriba de las farmacias (Por encima de la
                                    grilla)
                                  </label>
                                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer select-none">
                                    <input
                                      type="radio"
                                      name="locationBoxPosition"
                                      value="below"
                                      checked={
                                        customizeForm.locationBoxPosition ===
                                        "below"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          locationBoxPosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                    />
                                    Debajo de las farmacias (Al pie de la
                                    grilla)
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 7. CONFIGURATION OF PROMO / IMAGE / GIF MODULE (320x720 & 1230x140) */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-5">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 pb-3">
                              <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                7. Módulo Publicitario Adaptable (Celular 320x720 / Escritorio 1230x140)
                              </h3>
                              <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none bg-white px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                                <input
                                  type="checkbox"
                                  id="promoModuleEnabled"
                                  checked={!!customizeForm.promoModuleEnabled}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      promoModuleEnabled: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                />
                                <span>Activar módulo en la portada</span>
                              </label>
                            </div>

                            <p className="text-[11px] text-slate-500 font-sans leading-relaxed">
                              Permite mostrar banners publicitarios o informativos adaptados tanto para celulares (320x720) como para computadoras (1230x140).
                              Admite imágenes (PNG, JPG, WEBP) y animaciones (GIF).
                            </p>

                            <div className="space-y-6">
                              {/* Posición del Módulo */}
                              <div className="space-y-3">
                                <label className="block text-xs font-extrabold text-slate-700 font-sans uppercase tracking-wider text-[11px]">
                                  📍 Posición del módulo en la portada
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                  <label
                                    className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                                      customizeForm.promoModulePosition === "above_location"
                                        ? "bg-emerald-50/80 border-emerald-400 text-emerald-950 font-bold shadow-xs"
                                        : "bg-white border-slate-200 text-slate-600 font-medium hover:bg-slate-100/60"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="promoModulePosition"
                                      value="above_location"
                                      checked={customizeForm.promoModulePosition === "above_location"}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModulePosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer mt-0.5"
                                    />
                                    <div>
                                      <span className="block text-xs">Encima de la ubicación</span>
                                      <span className="block text-[10px] text-slate-400 font-normal">Por encima del buscador de distancias</span>
                                    </div>
                                  </label>

                                  <label
                                    className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                                      customizeForm.promoModulePosition === "below_location"
                                        ? "bg-emerald-50/80 border-emerald-400 text-emerald-950 font-bold shadow-xs"
                                        : "bg-white border-slate-200 text-slate-600 font-medium hover:bg-slate-100/60"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="promoModulePosition"
                                      value="below_location"
                                      checked={customizeForm.promoModulePosition === "below_location"}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModulePosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer mt-0.5"
                                    />
                                    <div>
                                      <span className="block text-xs">Debajo de la ubicación</span>
                                      <span className="block text-[10px] text-slate-400 font-normal">Justo después del buscador de distancias</span>
                                    </div>
                                  </label>

                                  <label
                                    className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                                      customizeForm.promoModulePosition === "above_pharmacies"
                                        ? "bg-emerald-50/80 border-emerald-400 text-emerald-950 font-bold shadow-xs"
                                        : "bg-white border-slate-200 text-slate-600 font-medium hover:bg-slate-100/60"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="promoModulePosition"
                                      value="above_pharmacies"
                                      checked={customizeForm.promoModulePosition === "above_pharmacies"}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModulePosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer mt-0.5"
                                    />
                                    <div>
                                      <span className="block text-xs">Arriba de las farmacias</span>
                                      <span className="block text-[10px] text-slate-400 font-normal">Encima de la grilla de guardias</span>
                                    </div>
                                  </label>

                                  <label
                                    className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${
                                      customizeForm.promoModulePosition === "below_pharmacies"
                                        ? "bg-emerald-50/80 border-emerald-400 text-emerald-950 font-bold shadow-xs"
                                        : "bg-white border-slate-200 text-slate-600 font-medium hover:bg-slate-100/60"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="promoModulePosition"
                                      value="below_pharmacies"
                                      checked={customizeForm.promoModulePosition === "below_pharmacies"}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModulePosition: e.target.value,
                                        })
                                      }
                                      className="text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer mt-0.5"
                                    />
                                    <div>
                                      <span className="block text-xs">Debajo de las farmacias</span>
                                      <span className="block text-[10px] text-slate-400 font-normal">Al pie de la grilla de guardias</span>
                                    </div>
                                  </label>
                                </div>
                              </div>

                              {/* Grid de Subida de Imágenes: Móvil y Escritorio */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-slate-200/60">
                                {/* Subida Móvil (320x720) */}
                                <div className="space-y-4 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-extrabold uppercase text-slate-700 tracking-wider font-mono">
                                      📱 Celular / Estándar (320x720)
                                    </span>
                                  </div>
                                  <ImageUploaderWidget
                                    label="Subir Foto o GIF para Celular"
                                    value={customizeForm.promoModuleImage || ""}
                                    showCompressionToggle={true}
                                    defaultCompress={true}
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        promoModuleImage: url,
                                      })
                                    }
                                    idPrefix="promo-module-upload-mobile"
                                  />

                                  <div className="space-y-1">
                                    <label className="block text-slate-500 uppercase font-bold font-mono text-[10px]">
                                      O ingresar URL directa (320x720)
                                    </label>
                                    <input
                                      type="url"
                                      value={customizeForm.promoModuleImage || ""}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModuleImage: e.target.value,
                                        })
                                      }
                                      placeholder="https://ejemplo.com/banner-320x720.gif"
                                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </div>

                                  {customizeForm.promoModuleImage && (
                                    <div className="border border-slate-200 rounded-xl p-2.5 bg-slate-50/50 space-y-1">
                                      <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                        Vista Previa Móvil (320x720)
                                      </span>
                                      <div className="max-w-[140px] mx-auto overflow-hidden rounded-xl border border-slate-200 bg-white flex items-center justify-center shadow-xs">
                                        <img
                                          src={customizeForm.promoModuleImage}
                                          alt="Preview Móvil"
                                          className="max-w-full h-auto max-h-[180px] object-contain block"
                                          referrerPolicy="no-referrer"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Subida Escritorio (1230x140) */}
                                <div className="space-y-4 bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-extrabold uppercase text-slate-700 tracking-wider font-mono">
                                      💻 Escritorio / PC (1230x140)
                                    </span>
                                  </div>
                                  <ImageUploaderWidget
                                    label="Subir Foto o GIF para Escritorio"
                                    value={customizeForm.promoModuleDesktopImage || ""}
                                    showCompressionToggle={true}
                                    defaultCompress={true}
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        promoModuleDesktopImage: url,
                                      })
                                    }
                                    idPrefix="promo-module-upload-desktop"
                                  />

                                  <div className="space-y-1">
                                    <label className="block text-slate-500 uppercase font-bold font-mono text-[10px]">
                                      O ingresar URL directa (1230x140)
                                    </label>
                                    <input
                                      type="url"
                                      value={customizeForm.promoModuleDesktopImage || ""}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          promoModuleDesktopImage: e.target.value,
                                        })
                                      }
                                      placeholder="https://ejemplo.com/banner-1230x140.png"
                                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </div>

                                  {customizeForm.promoModuleDesktopImage && (
                                    <div className="border border-slate-200 rounded-xl p-2.5 bg-slate-50/50 space-y-1">
                                      <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                        Vista Previa Escritorio (1230x140)
                                      </span>
                                      <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white flex items-center justify-center shadow-xs p-1">
                                        <img
                                          src={customizeForm.promoModuleDesktopImage}
                                          alt="Preview Escritorio"
                                          className="max-w-full h-auto max-h-[90px] object-contain block"
                                          referrerPolicy="no-referrer"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Opciones Adicionales de Enlace y Texto */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-200/60">
                                <div className="space-y-1">
                                  <label className="block text-slate-500 uppercase font-bold font-mono text-[10px]">
                                    Enlace de Redirección al hacer clic (Opcional)
                                  </label>
                                  <input
                                    type="url"
                                    value={customizeForm.promoModuleLink || ""}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        promoModuleLink: e.target.value,
                                      })
                                    }
                                    placeholder="https://wa.me/... o https://tusitio.com"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  />
                                </div>

                                <div className="space-y-1">
                                  <label className="block text-slate-500 uppercase font-bold font-mono text-[10px]">
                                    Texto Alternativo / Descripción (Opcional)
                                  </label>
                                  <input
                                    type="text"
                                    value={customizeForm.promoModuleAlt || ""}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        promoModuleAlt: e.target.value,
                                      })
                                    }
                                    placeholder="Módulo publicitario"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 8. CONFIGURATION OF CUSTOM FOOTER */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-5">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                              8. Configuración del Footer (Pie de Página)
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Columna Izquierda: Logo y datos de contacto */}
                              <div className="space-y-4 md:col-span-1 border-b md:border-b-0 md:border-r border-slate-200/60 pr-2 pb-4 md:pb-0">
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                  Lado Izquierdo (Contacto)
                                </span>

                                <div>
                                  <ImageUploaderWidget
                                    label="Logo del Footer"
                                    value={customizeForm.footerLogoUrl || ""}
                                    onChange={(url) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerLogoUrl: url,
                                      })
                                    }
                                    idPrefix="footer-logo"
                                  />
                                </div>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Dirección
                                  </label>
                                  <input
                                    type="text"
                                    value={customizeForm.footerAddress || ""}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerAddress: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. Av. España 450, Tandil"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Teléfono
                                  </label>
                                  <input
                                    type="text"
                                    value={customizeForm.footerPhone || ""}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerPhone: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. +54 249 444-4444"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Email / Correo
                                  </label>
                                  <input
                                    type="text"
                                    value={customizeForm.footerEmail || ""}
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerEmail: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. contacto@farmaturnotandil.com"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>
                              </div>

                              {/* Columna Central: Redes Sociales */}
                              <div className="space-y-4 md:col-span-1 border-b md:border-b-0 md:border-r border-slate-200/60 pr-2 pb-4 md:pb-0">
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                  Centro (Redes Sociales)
                                </span>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Texto Informativo
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      customizeForm.footerSocialsText || ""
                                    }
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerSocialsText: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. Seguinos en nuestras redes sociales:"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Enlace de Facebook
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      customizeForm.footerFacebookUrl || ""
                                    }
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerFacebookUrl: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. https://facebook.com/farmaturnotandil"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Enlace de Instagram
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      customizeForm.footerInstagramUrl || ""
                                    }
                                    onChange={(e) =>
                                      setCustomizeForm({
                                        ...customizeForm,
                                        footerInstagramUrl: e.target.value,
                                      })
                                    }
                                    placeholder="Ej. https://instagram.com/farmaturnotandil"
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                  />
                                </div>
                              </div>

                              {/* Columna Derecha: Estilo/Fondo del footer */}
                              <div className="space-y-4 md:col-span-1">
                                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                                  Estilo y Color
                                </span>

                                <div>
                                  <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                    Color de Fondo del Footer
                                  </label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="color"
                                      value={
                                        customizeForm.footerBgColor || "#1e293b"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          footerBgColor: e.target.value,
                                        })
                                      }
                                      className="h-10 w-16 border border-slate-200 rounded-lg cursor-pointer bg-white p-1"
                                    />
                                    <input
                                      type="text"
                                      value={
                                        customizeForm.footerBgColor || "#1e293b"
                                      }
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          footerBgColor: e.target.value,
                                        })
                                      }
                                      className="w-28 bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-xs font-mono font-semibold uppercase text-slate-800"
                                    />
                                  </div>

                                  {/* Quick presets for footer background */}
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {[
                                      "#1e293b",
                                      "#0f172a",
                                      "#111827",
                                      "#1c1917",
                                      "#1a2e05",
                                      "#162e3d",
                                      "#312e81",
                                    ].map((preset) => (
                                      <button
                                        key={preset}
                                        type="button"
                                        onClick={() =>
                                          setCustomizeForm({
                                            ...customizeForm,
                                            footerBgColor: preset,
                                          })
                                        }
                                        style={{ backgroundColor: preset }}
                                        className="h-5 w-5 rounded-full border border-white shadow-xs cursor-pointer active:scale-95 transition-transform"
                                        title={preset}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                                  <div>
                                    <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                      Texto de Derechos de Autor
                                    </label>
                                    <textarea
                                      value={customizeForm.footerCopyrightText}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          footerCopyrightText: e.target.value,
                                        })
                                      }
                                      rows={2}
                                      className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                    />
                                  </div>
                                  <div>
                                    <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase text-left">
                                      Enlace de Derechos de Autor
                                    </label>
                                    <input
                                      type="text"
                                      value={customizeForm.footerCopyrightLink}
                                      onChange={(e) =>
                                        setCustomizeForm({
                                          ...customizeForm,
                                          footerCopyrightLink: e.target.value,
                                        })
                                      }
                                      placeholder="Ej. https://mi-sitio.com"
                                      className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 6. SECURITY & ANALYTICS (Superadmin only) */}
                          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 space-y-4">
                            <h3 className="text-xs font-extrabold uppercase text-slate-700 tracking-wider font-mono flex items-center gap-1.5">
                              <span className="h-2 w-2 rounded-full bg-amber-500"></span>
                              6. Seguridad, Analítica & Gestión de
                              Administradores
                            </h3>
                            <p className="text-[11px] text-slate-500 leading-normal">
                              Configura el seguimiento estadístico para tu
                              portada con Google Analytics, define el ID de
                              Cliente para la autenticación y asigna un
                              administrador secundario con la dirección de
                              correo correspondiente.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Google Analytics ID */}
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase">
                                  ID de Medición de Google Analytics (G-XXXXXX)
                                </label>
                                <input
                                  type="text"
                                  placeholder="Ej. G-12345678"
                                  value={customizeForm.googleAnalyticsId || ""}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      googleAnalyticsId: e.target.value.trim(),
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-amber-500 text-slate-800"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 font-sans">
                                  Permite realizar un seguimiento estadístico en
                                  tiempo real de los vecinos de Tandil.
                                </p>
                              </div>

                              {/* Google Client ID */}
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[9px] font-bold uppercase">
                                  Google Client ID (OAuth 2.0)
                                </label>
                                <input
                                  type="text"
                                  placeholder="Ej. 123456-abcdef.apps.googleusercontent.com"
                                  value={customizeForm.googleClientId || ""}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      googleClientId: e.target.value.trim(),
                                    })
                                  }
                                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-amber-500 text-slate-800"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 font-sans">
                                  ID de cliente para habilitar el login de
                                  Google del personal de administración.
                                </p>
                              </div>
                            </div>

                            <div className="border-t border-slate-200 pt-4">
                              {/* Admin Email Assignment */}
                              <div>
                                <label className="block mb-1 text-slate-500 font-mono text-[10px] font-bold uppercase text-amber-600">
                                  Usuario Administrador Asignado (Gestión de
                                  Turnos)
                                </label>
                                <input
                                  type="email"
                                  placeholder="Ej. admin-tandil@gmail.com"
                                  value={customizeForm.adminEmail || ""}
                                  onChange={(e) =>
                                    setCustomizeForm({
                                      ...customizeForm,
                                      adminEmail: e.target.value.trim(),
                                    })
                                  }
                                  className="w-full bg-white border border-amber-300 rounded-lg px-3 py-2 text-xs font-semibold focus:outline-none focus:border-amber-500 text-slate-800"
                                />
                                <p className="text-[10px] text-slate-400 mt-1 font-sans">
                                  Ingresa la casilla de correo de Google del
                                  usuario secundario. Este usuario tendrá
                                  permisos exclusivamente para el calendario y
                                  la gestión de los turnos diarios.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Status messaging */}
                          {saveCustomizeStatus && (
                            <div
                              className={`p-4 rounded-xl text-xs font-bold border ${saveCustomizeStatus.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}
                            >
                              {saveCustomizeStatus.message}
                            </div>
                          )}

                          {/* Submit actions */}
                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                setCustomizeForm(frontpageConfig);
                                setSaveCustomizeStatus(null);
                              }}
                              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-500 text-xs font-bold rounded-lg transition-colors"
                            >
                              Revertir Cambios
                            </button>
                            <button
                              type="submit"
                              disabled={savingCustomize}
                              className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white text-xs font-extrabold rounded-lg transition-all shadow-sm flex items-center gap-1.5"
                            >
                              {savingCustomize && (
                                <RefreshCw size={12} className="animate-spin" />
                              )}
                              Guardar Configuración Visual
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          );
        })()}

      {/* MODAL 1: EDIT SINGLE DAY SHIFTS MAPPING (Slide pane / Sidebar edit concept) */}
      <AnimatePresence>
        {selectedDate && activeTab === "calendar" && (
          <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/45 backdrop-blur-xs">
            <motion.div
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              transition={{ type: "just" }}
              className="w-full max-w-md h-full bg-white border-l border-slate-200 p-6 shadow-xl flex flex-col justify-between overflow-y-auto text-slate-900"
            >
              <div className="space-y-6">
                {/* Header info */}
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <CalendarIcon size={18} className="text-emerald-500" />
                      Editar Turnos
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 font-medium">
                      Fecha:{" "}
                      <strong className="text-emerald-600 font-mono">
                        {DateTime.fromISO(selectedDate).toFormat(
                          "dd / MM / yyyy",
                        )}
                      </strong>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedDate(null);
                      setShiftSearchQuery("");
                    }}
                    className="p-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg text-slate-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Section 1: Assigned list for this day */}
                <div className="space-y-3">
                  <h4 className="text-xs font-extrabold uppercase tracking-wide text-slate-400 font-mono">
                    Asignadas de Turno
                  </h4>

                  {shifts.find((s) => s.date === selectedDate)?.pharmacyIds &&
                  shifts.find((s) => s.date === selectedDate)!.pharmacyIds
                    .length > 0 ? (
                    <div className="space-y-2">
                      {pharmacies
                        .filter((p) =>
                          shifts
                            .find((s) => s.date === selectedDate)!
                            .pharmacyIds.includes(p.id),
                        )
                        .map((pharm) => (
                          <div
                            key={pharm.id}
                            className="bg-slate-50 border border-slate-200 p-3 rounded-xl flex justify-between items-center"
                          >
                            <div>
                              <div className="text-xs font-bold uppercase text-slate-800 notranslate" translate="no">
                                {pharm.nombre}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5 font-medium">
                                {pharm.direccion}
                              </div>
                            </div>
                            <button
                              onClick={() =>
                                handleRemovePharmacyFromDate(pharm.id)
                              }
                              className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                              title="Remover de este turno"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-4 rounded-xl text-center italic font-medium">
                      No hay farmacias cargadas para la guardia de este día.
                    </div>
                  )}
                </div>

                {/* Section 2: Choose / Search master catalog to assign */}
                <div className="space-y-3">
                  <h4 className="text-xs font-extrabold uppercase tracking-wide text-slate-400 font-mono">
                    Asignar de la Base de Datos
                  </h4>

                  {/* Search query box to look filter list quickly */}
                  <div className="relative">
                    <Search
                      className="absolute left-2.5 top-2.5 text-slate-400"
                      size={14}
                    />
                    <input
                      type="text"
                      placeholder="Buscar farmacia por nombre en Tandil..."
                      value={shiftSearchQuery}
                      onChange={(e) => setShiftSearchQuery(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 pl-8 pr-3 text-xs text-slate-850 focus:outline-none focus:border-emerald-500 font-medium"
                    />
                  </div>

                  <div className="space-y-1.5 max-h-[220px] overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50/50">
                    {pharmacies
                      .filter((p) => {
                        const isQueryMatch = p.nombre
                          .toLowerCase()
                          .includes(shiftSearchQuery.toLowerCase());
                        const isNotAlreadyAssigned = !shifts
                          .find((s) => s.date === selectedDate)
                          ?.pharmacyIds.includes(p.id);
                        return isQueryMatch && isNotAlreadyAssigned;
                      })
                      .map((p) => (
                        <div
                          key={p.id}
                          onClick={() => handleAddPharmacyToDate(p.id)}
                          className="flex justify-between items-center p-2 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors group"
                        >
                          <div className="min-w-0 pr-2">
                            <div className="text-xs font-bold text-slate-700 group-hover:text-emerald-600 transition-colors uppercase notranslate" translate="no">
                              {p.nombre}
                            </div>
                            <div className="text-[10px] text-slate-450 mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis font-medium">
                              {p.direccion}
                            </div>
                          </div>
                          <button className="bg-white p-1 border border-slate-200 text-emerald-600 hover:bg-emerald-500 hover:text-white rounded-lg shrink-0 transition-colors">
                            <Plus size={12} />
                          </button>
                        </div>
                      ))}

                    {pharmacies.filter((p) => {
                      const isQueryMatch = p.nombre
                        .toLowerCase()
                        .includes(shiftSearchQuery.toLowerCase());
                      const isNotAlreadyAssigned = !shifts
                        .find((s) => s.date === selectedDate)
                        ?.pharmacyIds.includes(p.id);
                      return isQueryMatch && isNotAlreadyAssigned;
                    }).length === 0 && (
                      <div className="text-[11px] text-slate-400 text-center p-4 italic font-medium">
                        Sin coincidencias o todas asignadas.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom save info */}
              <div className="border-t border-slate-150 pt-4 mt-6">
                <button
                  onClick={() => {
                    setSelectedDate(null);
                    setShiftSearchQuery("");
                  }}
                  className="w-full py-2.5 bg-slate-900 border border-slate-800 rounded-xl hover:bg-slate-800 transition-colors text-xs font-bold font-sans text-white cursor-pointer shadow-sm"
                >
                  Finalizar Edición
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 2: REGISTER OR EDIT CATALOG ITEM */}
      <AnimatePresence>
        {editPharmacyMode && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 backdrop-blur-xs p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-md bg-white border border-slate-200 p-6 rounded-2xl shadow-xl relative space-y-4 text-slate-900"
            >
              <div className="flex justify-between items-center border-b border-slate-150 pb-3">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-widest font-mono">
                  {catalogForm.id
                    ? "Editar Farmacia"
                    : "Registrar Nueva Farmacia"}
                </h3>
                <button
                  onClick={() => setEditPharmacyMode(false)}
                  className="p-1 hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>

              <form
                onSubmit={handleCatalogFormSubmit}
                className="space-y-4 text-xs font-semibold text-slate-705"
              >
                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[10px]">
                    Nombre de la Farmacia
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. CENTRAL"
                    value={catalogForm.nombre}
                    onChange={(e) =>
                      setCatalogForm({ ...catalogForm, nombre: e.target.value })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 uppercase font-medium"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[10px]">
                    Dirección Eléctrica o de la Calle
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Av. Colón 100"
                    value={catalogForm.direccion}
                    onChange={(e) =>
                      setCatalogForm({
                        ...catalogForm,
                        direccion: e.target.value,
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[10px]">
                    Teléfono de Guardia
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. 2494636900"
                    value={catalogForm.telefono}
                    onChange={(e) =>
                      setCatalogForm({
                        ...catalogForm,
                        telefono: e.target.value,
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[10px]">
                    Número de WhatsApp de Consulta (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. 5492494112233"
                    value={catalogForm.whatsapp || ""}
                    onChange={(e) =>
                      setCatalogForm({
                        ...catalogForm,
                        whatsapp: e.target.value,
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Si la farmacia tiene WhatsApp, se habilitará un botón
                    "Enviar WhatsApp" para los vecinos.
                  </p>
                </div>

                <div className="space-y-2">
                  <ImageUploaderWidget
                    label="Foto de Fondo de la Farmacia"
                    value={catalogForm.fotoUrl || ""}
                    onChange={(url) =>
                      setCatalogForm({ ...catalogForm, fotoUrl: url })
                    }
                    idPrefix="catalog-pharmacy"
                  />
                  <div className="flex gap-2 items-center">
                    <span className="text-[10px] text-slate-400 font-bold uppercase shrink-0">
                      O Enlace Web Directo:
                    </span>
                    <input
                      type="text"
                      value={catalogForm.fotoUrl || ""}
                      onChange={(e) =>
                        setCatalogForm({
                          ...catalogForm,
                          fotoUrl: e.target.value,
                        })
                      }
                      placeholder="Ej. https://images.unsplash.com/..."
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-semibold focus:outline-none focus:border-emerald-500 text-slate-800"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400">
                    Imágenes de farmacias o de Unsplash quedan espectaculares
                    para la visualización del público. <br />
                    📐 **Medida ideal recomendada:** **400px a 800px** de ancho
                    (Ej: `600x400`). Fondo atenuado automático en listados.
                  </p>
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[10px]">
                    Color de la Tarjeta de Información (Hex)
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={catalogForm.colorHex || "#10b981"}
                      onChange={(e) =>
                        setCatalogForm({
                          ...catalogForm,
                          colorHex: e.target.value,
                        })
                      }
                      className="h-8 w-10 bg-transparent border-0 cursor-pointer"
                    />
                    <input
                      type="text"
                      placeholder="Ej. #10b981"
                      value={catalogForm.colorHex || "#10b981"}
                      onChange={(e) =>
                        setCatalogForm({
                          ...catalogForm,
                          colorHex: e.target.value,
                        })
                      }
                      className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-medium"
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4 border-t border-slate-150">
                  <button
                    type="button"
                    onClick={() => setEditPharmacyMode(false)}
                    className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg transition-colors text-xs font-bold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold rounded-lg transition-colors text-xs shadow-xs"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 3: META WHATSAPP CREDENTIAL CONFIG */}
      <AnimatePresence>
        {isConfigOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 backdrop-blur-xs">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg bg-white rounded-2xl p-6 shadow-xl flex flex-col justify-between max-h-[90vh] overflow-y-auto text-slate-900 mx-4 border border-slate-150"
            >
              <div className="flex items-center justify-between border-b border-slate-150 pb-3 mb-4">
                <div className="flex items-center gap-2">
                  <Settings size={18} className="text-emerald-500" />
                  <h3 className="text-sm font-bold text-slate-800">
                    Vincular WhatsApp de Meta Cloud
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsConfigOpen(false)}
                  className="p-1 hover:bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  <X size={14} />
                </button>
              </div>

              <form
                onSubmit={handleSaveConfig}
                className="space-y-4 text-xs font-semibold text-slate-700"
              >
                <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200 font-medium">
                  <p className="font-bold text-slate-700 mb-1">
                    💡 ¿Cómo conseguir estos datos de Meta?
                  </p>
                  1. Regístrate en{" "}
                  <span className="font-mono text-emerald-600">
                    developers.facebook.com
                  </span>{" "}
                  y crea una app de tipo "Otros" o "Negocios".
                  <br />
                  2. Agrega el producto <strong>WhatsApp</strong>.<br />
                  3. En la sección <strong>Configuración de API</strong> de
                  WhatsApp, encontrarás tu <strong>ID de Teléfono</strong>{" "}
                  (Phone ID) y podrás generar un{" "}
                  <strong>Token de Acceso</strong>.<br />
                  4. En la sección <strong>Configuración</strong> de Webhooks,
                  define tu URL del servidor y un{" "}
                  <strong>Token de Verificación</strong> personalizado (ej:{" "}
                  <em>"TandilVerify"</em>).
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[9px] tracking-wider">
                    ID de Teléfono de WhatsApp (PHONE_NUMBER_ID)
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. 104845550000000"
                    value={configForm.phoneId}
                    onChange={(e) =>
                      setConfigForm({ ...configForm, phoneId: e.target.value })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-mono text-xs font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 font-normal">
                    Identificador único del número de teléfono en tu cuenta Meta
                    Business.
                  </p>
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[9px] tracking-wider">
                    Token de Verificación de Webhook (VERIFY_TOKEN)
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. TandilVerify"
                    value={configForm.verifyToken}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        verifyToken: e.target.value,
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-mono text-xs font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 font-normal">
                    La clave String que configuras en Meta Webhooks para validar
                    la suscripción.
                  </p>
                </div>

                <div>
                  <label className="block mb-1 text-slate-500 uppercase font-bold font-mono text-[9px] tracking-wider">
                    Token de Acceso Permanente/Temporal (ACCESS_TOKEN)
                  </label>
                  <input
                    type="password"
                    placeholder="Escribe tu nuevo token para sobrescribir (déjalo vacío para mantener el actual)"
                    value={configForm.accessToken}
                    onChange={(e) =>
                      setConfigForm({
                        ...configForm,
                        accessToken: e.target.value,
                      })
                    }
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-850 focus:outline-none focus:border-emerald-500 font-mono text-xs font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-1 font-normal text-slate-450">
                    Token de acceso de Meta Developers para firmar y procesar
                    respuestas.
                  </p>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <label
                    htmlFor="argentinaFormatSelector"
                    className="block text-slate-700 font-bold text-[11px] select-none"
                  >
                    Formato de Número de Celular (Argentina)
                  </label>
                  <select
                    id="argentinaFormatSelector"
                    value={configForm.argentinaFormat}
                    onChange={(e) => {
                      const mode = e.target.value as any;
                      setConfigForm({
                        ...configForm,
                        argentinaFormat: mode,
                        keepArgentina9: mode === "keep9",
                      });
                    }}
                    className="w-full bg-white border border-slate-250 rounded-lg px-2 py-1.5 text-slate-800 text-[11.5px] font-medium focus:outline-none focus:border-emerald-500"
                  >
                    <option value="keep9">
                      Mantener "9" (Sandbox Internacional): ej. 5492494694726
                    </option>
                    <option value="add15">
                      Insertar "15" (Sandbox Nacional - Soluciona Error 131030):
                      ej. 54249154694726
                    </option>
                    <option value="strip9">
                      Quitar "9" (Estándar de Producción): ej. 542494694726
                    </option>
                  </select>
                  <p className="text-[10.5px] text-slate-500 leading-normal font-normal">
                    📌{" "}
                    <strong>
                      Solución al Error 131030 (Número no verificado):
                    </strong>{" "}
                    Si agregaste telf. en Sandbox de Meta como{" "}
                    <code>+54 9 249 469 4726</code> pero Meta exige la firma{" "}
                    <code>54249154694726</code>, selecciona{" "}
                    <strong>Insertar "15" (Sandbox Nacional)</strong>.
                  </p>
                </div>

                {saveConfigStatus && (
                  <div
                    className={`p-2.5 rounded-lg border text-center ${
                      saveConfigStatus.success
                        ? "bg-emerald-50 border-emerald-100 text-emerald-700"
                        : "bg-rose-50 border-rose-100 text-rose-700"
                    }`}
                  >
                    {saveConfigStatus.message}
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-4 border-t border-slate-150">
                  <button
                    type="button"
                    onClick={() => setIsConfigOpen(false)}
                    className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg transition-colors text-xs font-bold cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={savingConfig}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-lg transition-colors text-xs shadow-xs cursor-pointer flex items-center gap-1"
                  >
                    {savingConfig ? "Guardando..." : "Guardar Credenciales"}
                  </button>
                </div>
              </form>

              <WhatsAppActivation />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ORIGINAL EXCEL UPLOADER MODAL */}
      <AnimatePresence>
        {showOriginalExcelUploader && (
          <OriginalExcelUploader
            onClose={() => setShowOriginalExcelUploader(false)}
            onUploadSuccess={(msg) => {
              alert(msg);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
